import { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import {
  useNavigate,
  Routes,
  Route,
  useLocation,
  useNavigationType,
  Navigate,
} from 'react-router-dom';
import axios from 'axios';
import { chain, cloneDeep, debounce, isEmpty, sortBy, uniqueId } from 'lodash';
import moment from 'moment';

import OrderTypeSelection from '../../components/RestaurantMenu/OrderTypeSelection';
import RestaurantMenuHeader from '../../components/RestaurantMenu/RestaurantMenuHeader';
import CategoryTabs from '../../components/RestaurantMenu/CategoryTabs.js';
import SubCategories from '../../components/RestaurantMenu/SubCategories.js';
import MenuGrid from '../../components/RestaurantMenu/MenuGrid';
import OrderSummary from '../../components/RestaurantMenu/OrderSummary';
import OrderInformation from '../../components/RestaurantMenu/OrderInformation';
import GuestInformation from '../../components/RestaurantMenu/GuestInformation';
import YourItems from '../../components/RestaurantMenu/YourItems';
import {
  discountMessage,
  useDiscountCodeLookup,
} from '../../components/RestaurantMenu/DiscountCodeBlock';
import Payment from '../../components/RestaurantMenu/Payment';
import ProcessingOrderModal from '../../components/RestaurantMenu/ProcessingOrderModal.js';
import RestaurantAvailabilityModal from '../../components/RestaurantMenu/RestaurantAvailabilityModal.js';
import MenuNotAvailableModal from '../../components/RestaurantMenu/MenuNotAvailableModal';
import OrderNotAllowedModal from '../../components/RestaurantMenu/OrderNotAllowedModal';
import RushModeModal from '../../components/RestaurantMenu/RushModeModal.js';
import PromoPopupModal from '../../components/RestaurantMenu/PromoPopupModal.js';
import ValidationErrorModal from '../../components/RestaurantMenu/ValidationErrorModal.js';
import Loader from '../../components/Loader.js';
import { Modal } from '../../components/ui/Modal';
import { MultiSelectCheckbox } from '../../components/ui/MultiSelectCheckbox';
import { MultiCounter } from '../../components/ui/MultiCounter';
import { Counter } from '../../components/ui/Counter';
import { RadioGroup } from '../../components/ui/RadioGroup';
import ErrorModal from '../../components/RestaurantMenu/ErrorModal.js';

import configData from '../../config.json';
import {
  getRestaurantStatus,
  isCategoryAvailable,
  getDisabledTimes,
  getEarliestTime,
  isCategoryProperlyAvailable,
} from '../../utils/restaurant-availability.js';
import { renderCurrencyLanguage } from '../../utils';
import { useDataContext } from '../../context/data.js';
import { useOrderContext } from '../../context/order.js';
import { useNotificationContext } from '../../context/notification.js';
import { useRestaurantContext } from '../../context/restaurants.js';

import translations from '../../constants/translations.json';
import { isBlockedRestaurant } from '../../constants/blockedRestaurants';
import CapacityFullModal from '../../components/RestaurantMenu/CapacityFullModal';

import { useOrderStatusPolling } from '../../hooks/useOrderStatusPolling.js';
import { usePreorderPersistence } from '../../hooks/usePreorderPersistence.js';
import { useLagTime } from '../../hooks/useLagTime.js';

import { venueOrderTypeLabel } from '../../utils/orderTypes';
import './styles.css';
import { useMediaQuery } from '../../hooks/useMediaQuery.js';
import { useScrollSpy } from '../../hooks/useScrollSpy.js';
import {
  guestFacingError,
  serverMessage,
  subscriptionBlockedFromError,
} from '../../utils/serverError';
import OrderingPausedScreen, { orderingPausedText } from '../../components/OrderingPausedScreen';

const DEFAULT_IMG =
  'https://res.cloudinary.com/burgerkingnorway/image/upload/v1674745616/Heywaitr/1674745616-images.png';

// No discount code in play. A frozen module-level constant so clearing the code
// never hands the render tree a fresh object identity.
const EMPTY_DISCOUNT = Object.freeze({
  valid: false,
  code: '',
  amount: 0,
  type: null,
  value: 0,
});

export default function RestaurantMenu() {
  let pathname = window.location.pathname;
  const restaurantId = pathname.split('/')?.[2];
  const navigate = useNavigate();
  const location = useLocation();
  const navigationType = useNavigationType();

  const currentStep = useMemo(() => {
    const path = location.pathname.split('/').pop();
    const validSteps = ['menu', 'review', 'payment'];
    return validSteps.includes(path) ? path : 'menu';
  }, [location.pathname]);

  // Context Variables
  const {
    userProfile,
    setOrderFromRestaurantsNotAllowed,
    language,
    setLanguageFromRestaurant,
    clearRestaurantLanguageOverride,
  } = useDataContext();
  const { restaurant, setRestaurant } = useRestaurantContext();
  const orderContext = useOrderContext();

  // Hook Variables
  const isMobile = useMediaQuery('(max-width: 639px)');
  const { lagTime } = useLagTime();
  const {
    orderStatus,
    startPendingOrderPolling,
    startConfirmedOrderPolling,
    checkOrderStatus,
    payConfirm,
    setPayConfirm,
    isProcessingOrderModalOpen,
    setIsProcessingOrderModalOpen,
    cancelOrder,
    isCancelling,
  } = useOrderStatusPolling(userProfile);
  const { restoredData, hasRestoredData, persistOrderData, clearPersistedData } =
    usePreorderPersistence();

  // State Variables
  const [restaurantDescription, setRestaurantDescription] = useState('');
  const [orderItems, setOrderItems] = useState([]);
  const [orderType, setOrderType] = useState('dine-in');
  const [orderTypeOptions, setOrderTypeOptions] = useState([
    { id: 'dine-in', label: translations[language]?.orders_dine_in },
  ]);
  const [hasBag, setHasBag] = useState(false);
  const [hasDeliveryFee, setHasDeliveryFee] = useState(false);
  const [hasServiceFee, setHasServiceFee] = useState(false);
  const [hasFlatFee, setHasFlatFee] = useState(false);
  const [isWoltDeliveryEnabled, setIsWoltDeliveryEnabled] = useState(false);
  const [comments, setComments] = useState('');
  const [activeGroup, setActiveGroup] = useState('order-now');
  const [categories, setCategories] = useState([]);
  const [categoriesCopy, setCategoriesCopy] = useState([]);
  const [subCategories, setSubCategories] = useState([]);
  const [menuItems, setMenuItems] = useState([]);
  const [hasFetchedData, setHasFetchedData] = useState(false);
  // const [selectedCategory, setSelectedCategory] = useState(null);
  // const [selectedSubCategory, setSelectedSubCategory] = useState(null);
  const [tableNumbers, setTableNumbers] = useState([]);
  const [restaurantTips, setRestaurantTips] = useState([]);
  const [woltDelivery, setWoltDelivery] = useState(null);
  const [inAppLoading, setInAppLoading] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isErrorModalOpen, setIsErrorModalOpen] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [currentTime, setCurrentTime] = useState(() => moment());
  // State Variables for ItemSelection
  const [isItemSelectionModalOpen, setIsItemSelectionModalOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [itemQuantity, setItemQuantity] = useState(0);
  const [selectedExtras, setSelectedExtras] = useState([]);
  const [selectedAttributes, setSelectedAttributes] = useState([]);
  const [isValidationErrorModalOpen, setIsValidationErrorModalOpen] = useState(false);
  const [validationErrorMessage, setValidationErrorMessage] = useState('');
  const [itemComments, setItemComments] = useState('');
  // State Variables for OrderInformation
  const [isValidPhone, setIsValidPhone] = useState(true);
  const [tableNumber, setTableNumber] = useState(null);
  const [tipPercentage, setTipPercentage] = useState(null);
  const [phoneNumber, setPhoneNumber] = useState('');
  const [discountCode, setDiscountCode] = useState('');
  // Guest discount code. Same contract as the web checkout: the amount is the
  // SERVER's, and only the code STRING is sent with the order.
  const [discountApplied, setDiscountApplied] = useState(EMPTY_DISCOUNT);
  const [discountStatus, setDiscountStatus] = useState('idle'); // idle|checking|applied|error|unsupported
  const [discountError, setDiscountError] = useState('');
  const [discountApplyNonce, setDiscountApplyNonce] = useState(0);
  const [isCheckoutModalOpen, setIsCheckoutModalOpen] = useState(false);
  const [tipAmount, setTipAmount] = useState(0);
  const [pickup, setPickup] = useState('now');
  const [pickupOptions, setPickupOptions] = useState([
    { id: 'now', label: translations[language]?.pickup_now },
    { id: 'later', label: translations[language]?.pickup_later },
  ]);
  const [pickupDate, setPickupDate] = useState(new Date());
  const [pickupTime, setPickupTime] = useState('12:00');
  const [isPickupForTomorrow, setIsPickupForTomorrow] = useState(false);
  const [bagFee, setBagFee] = useState('4');
  const [deliveryFee, setDeliveryFee] = useState('50');
  const [woltDeliveryFee, setWoltDeliveryFee] = useState('');
  const [serviceFee, setServiceFee] = useState('');
  const [flatFee, setFlatFee] = useState('');
  const [roomNumber, setRoomNumber] = useState('');
  const [address, setAddress] = useState({ street: '', city: '', zipcode: '' });
  const [capacityFullModal, setCapacityFullModal] = useState({ open: false, nextSlot: null });
  // State Variables for GuestInformation
  const [guestInfo, setGuestInfo] = useState({
    name: '',
    email: '',
  });
  // State Variables for YourItems
  const [editingCartId, setEditingCartId] = useState(null);
  // State Variables for Payment
  const [tipId, setTipId] = useState(null);
  const [isViewOpeningTimesOpen, setIsViewOpeningTimesOpen] = useState(false);
  const [orderNotAllowed, setOrderNotAllowed] = useState(false);
  const [menuNotAvailable, setMenuNotAvailable] = useState(false);
  const [isRushModeModalOpen, setIsRushModeModalOpen] = useState(false);
  const [isPromoPopupModalOpen, setIsPromoPopupModalOpen] = useState(false);

  // Ref Variables
  const yourItemsRef = useRef(null);
  const orderInfoRef = useRef();
  const guestInfoRef = useRef();
  const addressRef = useRef();
  const paymentRef = useRef(null);
  const isRestoredNavigationRef = useRef(false);

  const hasHigherCategories = categories.length > 0;
  const pickupTimeInterval = 10;

  const groupedMenuItems = useMemo(() => {
    if (!menuItems?.length) return [];

    if (!hasHigherCategories) {
      // Group by category only when no higher categories
      return chain(menuItems)
        .groupBy('category')
        .map((items, category) => ({
          category,
          higherCategoryId: null,
          menu: items.sort((a, b) => (a.order || 0) - (b.order || 0)),
        }))
        .value();
    }

    // Build lookup: subCategoryId -> array of parent category _ids
    const subCategoryToParentIds = {};
    categories.forEach((category) => {
      category.TaggedCategories?.forEach((tagged) => {
        if (!subCategoryToParentIds[tagged._id]) {
          subCategoryToParentIds[tagged._id] = [];
        }
        subCategoryToParentIds[tagged._id].push(category._id);
      });
    });

    // Expand menu items to appear under all their parent categories
    const expandedMenuItems = [];
    menuItems.forEach((item) => {
      const parentIds = subCategoryToParentIds[item.categoryid] || [null];
      parentIds.forEach((parentId) => {
        expandedMenuItems.push({
          ...item,
          higherCategoryId: parentId,
        });
      });
    });

    // Group by both category and higherCategoryId
    return chain(expandedMenuItems)
      .groupBy((item) => `${item.higherCategoryId}_${item.category}`)
      .map((items) => ({
        category: items[0]?.category,
        higherCategoryId: items[0]?.higherCategoryId || null,
        menu: items.sort((a, b) => (a.order || 0) - (b.order || 0)),
      }))
      .sortBy((group) => {
        const parentCategory = categories.find((cat) => cat._id === group.higherCategoryId);
        return parentCategory?.Order ?? 999;
      })
      .value()
      .filter((item) => item.higherCategoryId);
  }, [menuItems, categories, hasHigherCategories]);

  // Filter menu items based on order type & hideondinein & hideontakeout
  const filteredGroupedMenuItems = useMemo(() => {
    return groupedMenuItems
      .filter((group) => {
        const subCategory = subCategories.find((sub) => sub.Category === group.category);
        if (!subCategory) return true; // Keep if no matching subcategory found

        if (subCategory.IsHidden) return false;
        if (orderType === 'dine-in' && subCategory.HideOnDineIn) return false;
        if (orderType === 'take-away' && subCategory.HideOnTakeOut) return false;

        return true;
      })
      .map((category) => ({
        ...category,
        menu: category.menu.filter((item) => {
          if (orderType === 'dine-in' && item.hideondinein) return false;
          if (orderType === 'take-away' && item.hideontakeout) return false;
          return true;
        }),
      }))
      .filter((category) => category.menu.length > 0)
      .sort((a, b) => {
        // Sort by parent category index first
        const parentIndexA = categories.findIndex((cat) => cat._id === a.higherCategoryId);
        const parentIndexB = categories.findIndex((cat) => cat._id === b.higherCategoryId);

        if (parentIndexA !== parentIndexB) {
          return (
            (parentIndexA === -1 ? 999 : parentIndexA) - (parentIndexB === -1 ? 999 : parentIndexB)
          );
        }

        // Then sort by subcategory order
        const subCatA = subCategories.find((sub) => sub.Category === a.category);
        const subCatB = subCategories.find((sub) => sub.Category === b.category);

        return (subCatA?.Order ?? 999) - (subCatB?.Order ?? 999);
      });
  }, [groupedMenuItems, orderType, categories, subCategories]);

  const scrollSpyData = useMemo(() => {
    return categories.map((cat) => ({
      ...cat,
      sections: filteredGroupedMenuItems
        .filter((g) => g.higherCategoryId === cat._id)
        .filter((g) =>
          subCategories
            .filter((subCategory) => {
              if (subCategory.IsHidden) return false;
              if (orderType === 'dine-in' && subCategory.HideOnDineIn) return false;
              if (orderType === 'take-away' && subCategory.HideOnTakeOut) return false;
              return true;
            })
            .some((sub) => sub.Category === g.category),
        ),
    }));
  }, [categories, filteredGroupedMenuItems, orderType]);

  const {
    activeCategory,
    activeSubCategory,
    scrollTo,
    subNavRef,
    setActiveCategory,
    setActiveSubCategory,
  } = useScrollSpy(scrollSpyData, {
    offset: 180,
    getCategoryId: (cat) => cat._id,
    getSections: (cat) => cat.sections,
    getSectionId: (section) => `${section.higherCategoryId}_${section.category}`,
  });

  const selectedCategory =
    categoriesCopy?.find((category) => category._id === activeCategory) || null;

  // Filter subcategories based on their category
  const filteredSubCategories = useMemo(() => {
    if (!subCategories.length) return [];

    // First, filter out hidden subcategories based on IsHidden and orderType
    const visibleSubCategories = subCategories.filter((subCategory) => {
      if (subCategory.IsHidden) return false;
      if (orderType === 'dine-in' && subCategory.HideOnDineIn) return false;
      if (orderType === 'take-away' && subCategory.HideOnTakeOut) return false;
      return true;
    });

    // If no higher categories, show all sub-categories that have menu items
    if (!hasHigherCategories) {
      return visibleSubCategories.filter((subCategory) =>
        filteredGroupedMenuItems.find((group) => group.category === subCategory.Category),
      );
    }

    // When higher categories exist
    if (!selectedCategory) return [];

    return visibleSubCategories.filter(
      (subCategory) =>
        selectedCategory.TaggedCategories.some((cat) => cat._id === subCategory._id) &&
        filteredGroupedMenuItems.find((group) => group.category === subCategory.Category),
    );
  }, [selectedCategory, subCategories, filteredGroupedMenuItems, hasHigherCategories, orderType]);

  const currentSubCategories = useMemo(() => {
    const currentCategory = scrollSpyData.find((cat) => cat._id === activeCategory);
    return currentCategory?.sections || [];
  }, [scrollSpyData, activeCategory]);

  // console.log('currentSubCategories', currentSubCategories);
  // console.log('scrollSpyData', scrollSpyData);

  // Get current sub-categories for active category
  const handleCategorySelect = (category) => {
    const _filteredGroupedMenuItems = filteredGroupedMenuItems.filter(
      (g) => g.higherCategoryId === category._id,
    );

    if (_filteredGroupedMenuItems?.length === 0) {
      setIsErrorModalOpen(true);
      setErrorMessage(translations[language]?.menu_grid_items_unavailable);
      return;
    }

    const firstSection = filteredGroupedMenuItems.find((g) => g.higherCategoryId === category._id);
    if (firstSection) {
      scrollTo(`${firstSection.higherCategoryId}_${firstSection.category}`, 180);
    }
  };

  const handleSubCategorySelect = (subCategory) => {
    const id = `${subCategory.higherCategoryId}_${subCategory.category || subCategory.Category || subCategory.name}`;
    scrollTo(id, 180);
  };

  const getRestaurantDescriptionTranslation = (item, language) => {
    return Object.keys(item?.Translation || {}).length > 0
      ? item.Translation?.[language]?.Description
      : item?.description || '';
  };

  const getCategoriesTranslation = (data, _language, restaurantLanguage) => {
    return data.map((category) => {
      let language = _language;

      if (
        Object.keys(category?.Translation || {}).length === 1 &&
        Object.keys(category?.Translation || {}).includes(restaurantLanguage)
      ) {
        language = restaurantLanguage;
      }

      if (!Object.keys(category?.Translation || {}).includes(language)) {
        language = Object.keys(category?.Translation || {})[0];
      }

      return {
        ...category,
        Category:
          Object.keys(category?.Translation || {}).length > 0
            ? category.Translation?.[language]?.Category || ''
            : category?.Category || '',
        Description:
          Object.keys(category?.Translation || {}).length > 0
            ? category.Translation?.[language]?.Description || ''
            : category?.Description || '',
      };
    });
  };

  const getSubCategoriesTranslation = (data, _language, restaurantLanguage) => {
    return data.map((subCategory) => {
      let language = _language;

      if (
        Object.keys(subCategory?.Translation || {}).length === 1 &&
        Object.keys(subCategory?.Translation || {}).includes(restaurantLanguage)
      ) {
        language = restaurantLanguage;
      }

      if (!Object.keys(subCategory?.Translation || {}).includes(language)) {
        language = Object.keys(subCategory?.Translation || {})[0];
      }

      return {
        ...subCategory,
        Category:
          Object.keys(subCategory?.Translation || {}).length > 0
            ? subCategory.Translation?.[language]?.Category || ''
            : subCategory?.Category || '',
      };
    });
  };

  const getMenuTranslation = (data, data2, _language, restaurantLanguage) => {
    const translatedMenuItems = data.map((item) => {
      let language = _language;

      if (
        Object.keys(item?.Translation || {}).length === 1 &&
        Object.keys(item?.Translation || {}).includes(restaurantLanguage)
      ) {
        language = restaurantLanguage;
      }

      if (!Object.keys(item?.Translation || {}).includes(language)) {
        language = Object.keys(item?.Translation || {})[0];
      }

      return {
        ...item,
        name:
          Object.keys(item?.Translation || {}).length > 0
            ? item.Translation?.[language]?.Name || ''
            : item?.name || '',
        description:
          Object.keys(item?.Translation || {}).length > 0
            ? item.Translation?.[language]?.Description || ''
            : item?.description || '',
      };
    });

    const translatedSubCategories = data2.map((subCategory) => {
      let language = _language;

      if (
        Object.keys(subCategory?.Translation || {}).length === 1 &&
        Object.keys(subCategory?.Translation || {}).includes(restaurantLanguage)
      ) {
        language = restaurantLanguage;
      }

      if (!Object.keys(subCategory?.Translation || {}).includes(language)) {
        language = Object.keys(subCategory?.Translation || {})[0];
      }

      return {
        ...subCategory,
        Category:
          Object.keys(subCategory?.Translation || {}).length > 0
            ? subCategory.Translation?.[language]?.Category || ''
            : subCategory?.Category || '',
      };
    });

    return translatedMenuItems?.map((item, index, array) => {
      let language = _language;

      const itemFind = translatedSubCategories.find(
        (subCategory) => subCategory._id === item.categoryid,
      );

      if (itemFind) {
        const attributes =
          item?.attributes.map((attribute) => {
            const attributeFind = translatedSubCategories.find(
              (subCategory) => subCategory._id === attribute._id,
            );
            const options =
              attribute?.options.map((option) => {
                let result = { ...option };

                const optionFind = translatedSubCategories.find(
                  (subCategory) => subCategory._id === (option?.maincategoryid || null),
                );
                const optionFind2 = translatedSubCategories.find(
                  (subCategory) => subCategory._id === (option?.CategoryId || null),
                );
                const optionFind3 = array.find(
                  (menuItem) => menuItem._id === (option?._id || null),
                );

                if (optionFind) {
                  result = {
                    ...result,
                    maincategory: optionFind.Category || '',
                  };
                }

                if (optionFind2) {
                  result = {
                    ...result,
                    Category: optionFind2.Category || '',
                  };
                }

                if (optionFind3) {
                  result = {
                    ...result,
                    name: optionFind3.name || '',
                    Name: optionFind3.name || '',
                  };
                }

                if (result.minlimit) {
                  return {
                    ...result,
                    minlimit: Number(result.minlimit) || null,
                  };
                }

                return result;
              }) || [];

            if (attributeFind) {
              return {
                ...attribute,
                name: attributeFind.Category || '',
                options: attribute?.options?.length > 0 ? options : [],
              };
            }

            return { ...attribute, options: attribute?.options?.length > 0 ? options : [] };
          }) || [];

        return {
          ...item,
          category: itemFind.Category || '',
          categorydesc: itemFind.Description || '',
          attributes: item?.attributes?.length > 0 ? attributes : [],
        };
      }

      return item;
    });
  };

  const getOrderItemsTranslation = (data, _language, restaurantLanguage) => {
    return (
      data?.map((orderItem) => {
        let language = _language;

        if (
          Object.keys(orderItem?.Translation || {}).length === 1 &&
          Object.keys(orderItem?.Translation || {}).includes(restaurantLanguage)
        ) {
          language = restaurantLanguage;
        }

        if (!Object.keys(orderItem?.Translation || {}).includes(language)) {
          language = Object.keys(orderItem?.Translation || {})[0];
        }

        return {
          ...orderItem,
          name:
            Object.keys(orderItem?.Translation || {}).length > 0
              ? orderItem.Translation?.[language]?.Name || ''
              : orderItem?.name || '',
          description:
            Object.keys(orderItem?.Translation || {}).length > 0
              ? orderItem.Translation?.[language]?.Description || ''
              : orderItem?.description || '',
        };
      }) || []
    );
  };

  useEffect(() => {
    if (typeof window === 'undefined') return;

    if (isBlockedRestaurant(restaurantId)) {
      navigate('/', { replace: true });
      return;
    }

    let language = 'en';
    const userLocationCache = window.localStorage.getItem('position');
    const userLocation = JSON.parse(userLocationCache);

    const fetchData = async () => {
      try {
        setLoading(true);
        const [
          restaurantData,
          categoriesData,
          subCategoriesData,
          menuData,
          tableNumberData,
          tipsData,
        ] = await Promise.all([
          axios.post(
            `${configData.SERVER_URL}/mobile/restaurant/fetchspecific?id=${restaurantId}`,
            {
              id: restaurantId,
            },
            { headers: { Authorization: `Bearer ${userProfile?.token}` } },
          ),
          axios.get(`${configData.SERVER_URL}/highercategory/fetch?restaurant=${restaurantId}`),
          axios.get(`${configData.SERVER_URL}/menu/category/fetch?restaurant=${restaurantId}`),
          axios.get(
            `${configData.SERVER_URL}/menu/fetch?restaurant=${restaurantId}&category=All`,
            'no_higher_cat',
          ),
          axios.get(`${configData.SERVER_URL}/tableno/mobile/fetch?restaurant=${restaurantId}`, {
            id: restaurantId,
          }),
          axios.get(`${configData.SERVER_URL}/tips/mobile/fetch?restaurant=${restaurantId}`, {
            Authorization: `Bearer ${userProfile?.token}`,
          }),
        ]);

        // Process restaurant data
        const processedRestaurant = {
          ...restaurantData.data,
          availability: restaurantData.data.AvailabilityOriginal,
        };

        setLanguageFromRestaurant(processedRestaurant);

        if (processedRestaurant?.currency.code) {
          language = renderCurrencyLanguage(processedRestaurant?.currency.code);
        }

        if (window.localStorage.getItem('user_lang')) {
          language = window.localStorage.getItem('user_lang');
        }

        const processedSubCategories = getSubCategoriesTranslation(
          subCategoriesData.data,
          language,
          renderCurrencyLanguage(processedRestaurant?.currency.code),
        );

        // Get menu items
        const menuItems = getMenuTranslation(
          menuData.data.data,
          subCategoriesData.data,
          language,
          renderCurrencyLanguage(processedRestaurant?.currency.code),
        );

        // Process existing higher categories with availability
        const processedCategoriesData = (categoriesData.data || [])
          .filter((category) => !category.IsAddOn)
          .map((category) => ({
            ...category,
            availability: category.Availability,
            isAvailable: isCategoryAvailable(category),
          }));
        const processedCategories = getCategoriesTranslation(
          processedCategoriesData,
          language,
          renderCurrencyLanguage(processedRestaurant?.currency.code),
        );

        // Find higher categories that exist in the menu items but not in the fetched categories
        const existingCategoryIds = new Set(
          processedCategories.flatMap(
            (cat) => cat.TaggedCategories?.map((tagged) => tagged._id) || [],
          ),
        );

        // Group items by their categoryid to find potential missing higher categories
        const categoryGroups = {};
        menuItems.forEach((item) => {
          if (item.categoryid && !existingCategoryIds.has(item.categoryid)) {
            if (!categoryGroups[item.category]) {
              categoryGroups[item.category] = {
                name: item.category,
                items: [],
              };
            }
            categoryGroups[item.category].items.push(item);
          }
        });

        // Process menu items
        const sortedMenu = sortBy(menuItems, ['category']);

        // Sort sub-categories
        const sortedSubCategories = sortBy(processedSubCategories, ['Order'], ['asc']);

        // Combine existing and new higher categories
        const allCategories = [...processedCategories];

        // Translate restaurant's description
        const restaurantDescription = processedRestaurant?.Translation
          ? processedRestaurant.Translation?.[language]?.Description
          : processedRestaurant?.description || '';

        const filteredCategories = allCategories.filter((category) =>
          isCategoryProperlyAvailable(category),
        );

        setRestaurant(processedRestaurant);
        setRestaurantDescription(restaurantDescription);
        setCategories(
          allCategories.filter((category) => {
            if (processedRestaurant.isdinein) {
              return isCategoryProperlyAvailable(category);
            } else if (processedRestaurant.istakeout) {
              return isCategoryProperlyAvailable(category);
            }

            return category;
          }),
        );
        setCategoriesCopy(allCategories);
        setActiveCategory(filteredCategories?.[0]?._id || null);
        setMenuItems(sortedMenu);
        setSubCategories(sortedSubCategories);

        // Set default order type options based on restaurant settings
        const orderTypeOptions = [];
        if (processedRestaurant.isdinein) {
          orderTypeOptions.push({
            id: 'dine-in',
            label:
              venueOrderTypeLabel('dine-in', language, processedRestaurant) ||
              translations[language]?.orders_dine_in,
          });
        }

        // if (processedRestaurant.istakeout) {
        //   orderTypeOptions.push({
        //     id: 'take-away',
        //     label: translations[language]?.orders_take_away,
        //   });
        // }

        // if (processedRestaurant.EnableWoltDelivery) {
        //   orderTypeOptions.push({
        //     id: 'delivery',
        //     label: translations[language]?.orders_delivery,
        //   });
        // }

        const lastOrderId = localStorage.getItem('hw_lastorderid');

        if (!isRestoredNavigationRef.current) {
          if (orderTypeOptions?.length > 1) {
            if (lastOrderId) {
              localStorage.removeItem('hw_lastorderid');
              navigateReplace('menu');
            } else {
              navigateReplace('menu');
            }
          } else {
            navigateReplace('menu');
          }
        }

        setOrderTypeOptions(orderTypeOptions || []);

        // Set default pickup options that will depend on allowAdvanceOrder
        const pickupOptions = [{ id: 'now', label: translations[language]?.pickup_now }];
        if (processedRestaurant.allowAdvanceOrder) {
          pickupOptions.push({ id: 'later', label: translations[language]?.pickup_later });
        }

        setPickupOptions(pickupOptions || []);

        // Set default order type based on restaurant settings
        if (processedRestaurant.isdinein) {
          setOrderType('dine-in');
          setHasBag(false);
          setHasDeliveryFee(false);
        } else if (processedRestaurant.istakeout) {
          setOrderType('take-away');
          if (processedRestaurant.allowAdvanceOrder) {
            setPickup('later');
          }

          if (!processedRestaurant.HasDeliveryFee) {
            setHasDeliveryFee(false);
          }

          if (!processedRestaurant.BagTakeAway) {
            setHasBag(false);
          }
        }

        // Set default fee values from restaurant
        if (processedRestaurant.BagTakeAwayPrice) {
          setBagFee(processedRestaurant.BagTakeAwayPrice);
        }

        if (processedRestaurant.DeliveryFee) {
          setDeliveryFee(processedRestaurant.DeliveryFee);
        }

        if (processedRestaurant.serviceFee) {
          setHasServiceFee(true);
          setServiceFee(processedRestaurant.serviceFee);
        } else {
          setHasServiceFee(false);
        }

        if (processedRestaurant.flatFee) {
          setHasFlatFee(true);
          setFlatFee(processedRestaurant.flatFee);
        } else {
          setHasFlatFee(false);
        }

        const tableNumbers = tableNumberData.data.map((table) => {
          return {
            label: table.number,
            value: table.number,
            ...table,
          };
        });

        setTableNumbers(tableNumbers);
        setTableNumber(tableNumbers?.[0] || null);
        setRestaurantTips(tipsData.data);
      } catch (error) {
        console.log('error', error);
        setError(error);
        console.error('Error fetching restaurant data:', error);
      } finally {
        setLoading(false);
      }
    };

    if (restaurantId) {
      fetchData();
    }

    // // Always set the step upon page load
    // navigateReplace('menu');

    localStorage.setItem('return_url', pathname);

    //check last order
    orderContext.checkPendingOrder();

    // Cleanup function to clear restaurant language override when leaving
    return () => {
      clearRestaurantLanguageOverride();
    };
  }, [restaurantId]);

  // Load cart from localStorage
  useEffect(() => {
    const savedCart = localStorage.getItem('hw_cart');
    if (savedCart) {
      try {
        const parsedCart = JSON.parse(savedCart);
        if (restaurantId === parsedCart.restaurantId) {
          setOrderItems(parsedCart.items || []);
          setOrderType(parsedCart.orderType || 'dine-in');
          setComments(parsedCart.comments || '');
        }
      } catch (e) {
        console.error('Error parsing saved cart:', e);
      }
    }
  }, [restaurantId]);

  useEffect(() => {
    if (!language) return;

    const translateOptions = (options, translationKeys) =>
      options?.map((item) => ({
        ...item,
        label: translationKeys[item.id] || item.label,
      })) || [];

    setOrderTypeOptions(
      translateOptions(orderTypeOptions, {
        'dine-in':
          venueOrderTypeLabel('dine-in', language, restaurant) ||
          translations[language]?.orders_dine_in,
        'take-away':
          venueOrderTypeLabel('take-away', language, restaurant) ||
          translations[language]?.orders_take_away,
      }),
    );

    setPickupOptions(
      translateOptions(pickupOptions, {
        now: translations[language]?.pickup_now,
        later: translations[language]?.pickup_later,
      }),
    );

    const translatedRestaurantDescription = getRestaurantDescriptionTranslation(
      restaurant,
      language,
    );
    const translatedCategories = getCategoriesTranslation(
      categories,
      language,
      renderCurrencyLanguage(restaurant?.currency.code),
    );
    const translatedCategoriesCopy = getCategoriesTranslation(
      categoriesCopy,
      language,
      renderCurrencyLanguage(restaurant?.currency.code),
    );
    const translatedMenuItems = getMenuTranslation(
      menuItems,
      subCategories,
      language,
      renderCurrencyLanguage(restaurant?.currency.code),
    );
    const translatedSubCategories = getSubCategoriesTranslation(
      subCategories,
      language,
      renderCurrencyLanguage(restaurant?.currency.code),
    );
    const translatedOrderItems = getOrderItemsTranslation(
      orderItems,
      language,
      renderCurrencyLanguage(restaurant?.currency.code),
    );

    setRestaurantDescription(translatedRestaurantDescription);
    setCategories(translatedCategories);
    setCategoriesCopy(translatedCategoriesCopy);
    setMenuItems(translatedMenuItems);
    setSubCategories(translatedSubCategories);
    setOrderItems(translatedOrderItems);
  }, [restaurant, language]);

  // Save cart to localStorage
  useEffect(() => {
    if (restaurant) {
      const cartData = {
        items: orderItems,
        orderType,
        hasBag,
        hasDeliveryFee,
        comments,
        restaurantId,
        timestamp: new Date().toISOString(),
      };

      localStorage.setItem('hw_cart', JSON.stringify(cartData));
    }
  }, [orderItems, orderType, hasBag, hasDeliveryFee, comments, restaurant, restaurantId]);

  // Handle order type change - filter incompatible items
  useEffect(() => {
    if (hasFetchedData) {
      if (orderItems.length > 0) {
        const filteredItems = orderItems
          .filter((item) =>
            subCategories
              .filter((subCategory) => {
                if (subCategory.IsHidden) return false;
                if (orderType === 'dine-in' && subCategory.HideOnDineIn) return false;
                if (orderType === 'take-away' && subCategory.HideOnTakeOut) return false;
                return true;
              })
              .some((sub) => sub._id === item.categoryid),
          )
          .filter((item) => {
            // Filter out items that don't work with current order type
            if (orderType === 'dine-in' && item.hideontakeout) {
              return false;
            }
            if (orderType === 'take-away' && item.hideondinein) {
              return false;
            }
            return true;
          });

        if (filteredItems.length !== orderItems.length) {
          setOrderItems(filteredItems);

          if (filteredItems.length === 0 && currentStep !== 'menu') {
            navigateToStep('menu');
          }
        }
      }

      if (orderType === 'dine-in') {
        const filteredCategories = categoriesCopy.filter((category) =>
          isCategoryProperlyAvailable(category),
        );

        setCategories(filteredCategories);

        const hiddenCategories = categoriesCopy.filter(
          (category) => !isCategoryProperlyAvailable(category),
        );

        // Pick the first available category as fallback
        const fallback = filteredCategories?.[0]?._id || null;

        // If the previously selected category is hidden, use fallback
        setActiveCategory((prev) => {
          const isPrevHidden = prev && hiddenCategories.some((h) => h._id === prev._id);
          return isPrevHidden ? fallback : prev;
        });
      } else if (orderType === 'take-away') {
        const filteredCategories = categoriesCopy.filter((category) =>
          isCategoryProperlyAvailable(category),
        );

        setCategories(filteredCategories);

        const hiddenCategories = categoriesCopy.filter(
          (category) => !isCategoryProperlyAvailable(category),
        );

        // Pick the first available category as fallback
        const fallback = filteredCategories?.[0]?._id || null;

        // If the previously selected category is hidden, use fallback
        setActiveCategory((prev) => {
          const isPrevHidden = prev && hiddenCategories.some((h) => h._id === prev._id);
          return isPrevHidden ? fallback : prev;
        });
      }
    }
  }, [orderType, categoriesCopy]);

  useEffect(() => {
    // Extract phone number from user profile if available
    if (userProfile?.profile?.mobilenumber) {
      let number = userProfile.profile.mobilenumber;
      if (
        number?.toString?.()?.substring?.(0, 2) == '45' ||
        number?.toString?.()?.substring?.(0, 3) == '+45'
      ) {
        setPhoneNumber(userProfile.profile.mobilenumber);
      } else {
        setPhoneNumber('45' + number);
      }
    }
  }, [userProfile]);

  useEffect(() => {
    if (typeof window === 'undefined') return;

    if (!restaurant?.RushMode) return;

    // Check URL params to see if we should hide rush mode
    const params = new URLSearchParams(window.location.search);
    if (params.get('hideRushMode')) return;

    setIsRushModeModalOpen(true);
  }, [restaurant?.RushMode]);

  useEffect(() => {
    if (typeof window === 'undefined') return;

    if (!restaurant?.PromoPopUpEnabled) return;

    setIsPromoPopupModalOpen(true);
  }, [restaurant?.PromoPopUpEnabled]);

  useEffect(() => {
    if (typeof window === 'undefined') return;

    // Check URL params to see if we should hide rush mode
    const params = new URLSearchParams(window.location.search);
    if (params.get('fromKiosk')) {
      handleSetGuestUUID();
      setIsProcessingOrderModalOpen(true);
      setTimeout(() => setIsProcessingOrderModalOpen(false), 12000);
    }
  }, []);

  // Check for existing order on component mount
  useEffect(() => {
    const checkExistingOrder = async () => {
      const cachedOrder = localStorage.getItem('hw_pending_orderv2');
      if (cachedOrder) {
        const order = JSON.parse(cachedOrder);
        setPayConfirm(order);

        // Check if we need to show the processing modal
        if (!order.status) {
          navigateToStep('menu');
        }

        // You can also check the current status immediately
        const currentStatus = await checkOrderStatus(order._id);
        if (currentStatus) {
          // Handle status update
          if (currentStatus.type === 'order_accepted') {
            setPayConfirm({ ...order, status: 'confirmed' });
          } else if (currentStatus.type === 'order_cancelled') {
            localStorage.removeItem('hw_pending_orderv2');
            setPayConfirm({});
            setIsProcessingOrderModalOpen(false);
          }
        } else {
          localStorage.removeItem('hw_pending_orderv2');
          setPayConfirm({});
          setIsProcessingOrderModalOpen(false);
        }
      }
    };

    checkExistingOrder();
  }, [restaurant, checkOrderStatus]);

  useEffect(() => {
    return () => {
      // Cleanup on unmount
      localStorage.removeItem('hw_cart');
      // The polling cleanup is handled in the hook itself
    };
  }, []);

  useEffect(() => {
    handleSetGuestUUID();
  }, []);

  const handleSetGuestUUID = () => {
    const uuid = new Date().getTime();
    localStorage.setItem('guest-uui', uuid);
  };

  // // Handle browser back/forward navigation
  // useEffect(() => {
  //   const handlePopState = (event) => {
  //     const hasActiveOrder = orderItems.length > 0 && currentStep !== 'choose-order';

  //     if (hasActiveOrder && currentStep === 'menu') {
  //       // If user is trying to leave the menu with items in cart
  //       event.preventDefault();
  //       setShowBackNavigationModal(true);
  //       // Push the current state back to prevent navigation
  //       window.history.pushState(null, '', location.pathname);
  //     }
  //   };

  //   // Listen for browser navigation
  //   window.addEventListener('popstate', handlePopState);

  //   return () => {
  //     window.removeEventListener('popstate', handlePopState);
  //   };
  // }, [orderItems.length, currentStep, location.pathname]);

  // Apply restored data when available
  useEffect(() => {
    if (typeof window === 'undefined') return;

    const params = new URLSearchParams(window.location.search);
    const isPersistData = params.get('persistData');

    if (isPersistData && hasRestoredData && restoredData) {
      isRestoredNavigationRef.current = true;
      setIsProcessingOrderModalOpen(false);

      // Apply all restored values to state
      setOrderType(restoredData.orderType || 'dine-in');
      setPickup(restoredData.pickup || 'now');
      setTimeout(() => navigateReplace(restoredData.currentStep || 'menu'), 100);
      if (restoredData.pickupDate) setPickupDate(new Date(restoredData.pickupDate));
      if (restoredData.pickupTime) setPickupTime(restoredData.pickupTime);
      if (restoredData.phoneNumber) handlePhoneNumberChange(restoredData.phoneNumber);
      if (restoredData.tableNumber) handleTableNumberChange(restoredData.tableNumber);
      if (restoredData.comments) handleCommentsChange(restoredData.comments);
      if (restoredData.tipId) setTipId(restoredData.tipId);
      if (restoredData.tipPercentage) handleTipPercentageChange(restoredData.tipPercentage);
      if (restoredData.orderItems?.length) setOrderItems(restoredData.orderItems);
      // Only the code string was persisted. Putting it back feeds it straight
      // into the lookup, which re-validates it against the restored cart — the
      // stored amount is never trusted, because there never was one.
      if (restoredData.discountCode) {
        setDiscountCode(restoredData.discountCode);
        setDiscountStatus('checking');
      }
      if (restoredData.hasBag !== undefined) handleBagToggle(restoredData.hasBag);
      if (restoredData.hasDeliveryFee !== undefined)
        handleDeliveryFeeToggle(restoredData.hasDeliveryFee);
      if (restoredData.roomNumber) handleRoomNumberChange(restoredData.roomNumber);
      if (restoredData.guestInfo) {
        setGuestInfo(
          restoredData?.guestInfo || {
            name: '',
            email: '',
          },
        );
      }
      if (restoredData.guestId) {
        orderContext.setGuestId(restoredData.guestId || '');
        orderContext.setIsGuest(true);
        localStorage.setItem('guest-uui', restoredData.guestId);
      }

      // Clear after applying
      clearPersistedData();
    }
  }, [hasRestoredData, restoredData, clearPersistedData]);

  // Reset selectedSubCategory when selectedCategory changes
  useEffect(() => {
    if (filteredSubCategories.length > 0) {
      const firstSubCategory =
        filteredSubCategories[0].category ||
        filteredSubCategories[0].Category ||
        filteredSubCategories[0].name;

      setActiveSubCategory(firstSubCategory);
    } else {
      setActiveSubCategory(null);
    }
  }, [selectedCategory, filteredSubCategories]);

  useEffect(() => {
    if (selectedCategory) {
      if (!selectedCategory.isAvailable) {
        if (restaurant.istakeout && !restaurant.isdinein) {
          setOrderType('take-away');
          if (!restaurant.allowAdvanceOrder) {
            return;
          }
          setPickup('later');
        }
      }

      // if (
      //   !restaurant.isOpen &&
      //   restaurant.istakeout &&
      //   !restaurant.isdinein &&
      //   restaurant.allowAdvanceOrder
      // ) {
      //   setOrderType('take-away');
      //   setPickup('later');
      // }
    }
  }, [selectedCategory]);

  useEffect(() => {
    if (typeof window !== 'undefined' && typeof window.scrollTo === 'function') {
      try {
        window.scrollTo(0, 0);
      } catch (err) {
        window.scrollTo(0, 0);
      }
    }
  }, [currentStep]);

  // Sync with actual clock minutes
  useEffect(() => {
    const syncToMinute = () => {
      setCurrentTime(moment());
    };

    // Calculate ms until next minute starts
    const now = new Date();
    const msUntilNextMinute = (60 - now.getSeconds()) * 1000 - now.getMilliseconds();

    // First timeout to sync to the next minute
    const timeoutId = setTimeout(() => {
      syncToMinute();
      // Then interval every 60 seconds
      intervalId = setInterval(syncToMinute, 60000);
    }, msUntilNextMinute);

    let intervalId;

    return () => {
      clearTimeout(timeoutId);
      if (intervalId) clearInterval(intervalId);
    };
  }, []);

  const navigateToStep = (step) => {
    navigate(`/restaurant-kiosk/${restaurantId}/${step}`, { replace: false });
  };

  const navigateReplace = (step) => {
    navigate(`/restaurant-kiosk/${restaurantId}/${step}`, { replace: true });
  };

  // Once an order is placed, Back can land the customer on a stale review/payment
  // step because those pages are still in browser history. Always redirect, but
  // only alert on a real Back/Forward press — placing an order empties the cart
  // and routes here itself, and orderItems is empty on every mount until the
  // saved cart rehydrates, so the first pass stays silent too.
  const hasNavigatedRef = useRef(false);
  useEffect(() => {
    const isBackNavigation = navigationType === 'POP' && hasNavigatedRef.current;
    hasNavigatedRef.current = true;

    if ((currentStep === 'review' || currentStep === 'payment') && orderItems.length === 0) {
      navigateReplace('menu');
      if (isBackNavigation) {
        alert(`${translations[language]?.restaurant_menu_empty_cart}!`);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentStep, orderItems.length, navigationType]);

  // Ordering paused (the venue's heywaitr subscription is blocked): the menu
  // stays browsable, every way into an order stops here with the backend's own
  // wording. Same gate as the guest menu (pages/RestaurantMenu).
  const showOrderingPaused = useCallback(
    (message) => {
      setErrorMessage(orderingPausedText(language, message || restaurant?.orderingPausedMessage));
      setIsErrorModalOpen(true);
    },
    [restaurant, language],
  );

  // Item selection modal functions
  const handleItemSelectionModalOpen = (item, cartItemId = null) => {
    if (restaurant.orderingPaused) {
      showOrderingPaused();
      return;
    }

    if (!restaurant.isMenuBuyable) {
      return;
    }

    if (!restaurant.isdinein && !restaurant.istakeout) {
      return;
    }

    if (toggleOrderIfNotAllowed()) {
      setOrderNotAllowed(true);
      return;
    }

    if (checkIfCategoryIsDisabled()) {
      setMenuNotAvailable(true);
      return;
    }

    if (isUserAllowedToOrder) {
      // If a specific cartItemId is provided, we're editing an existing cart item
      if (cartItemId) {
        const existingItem = orderItems.find((orderItem) => orderItem.cartId === cartItemId);
        const menuItem = menuItems.find((menuItem) => menuItem._id === item._id);

        if (existingItem && menuItem) {
          setSelectedItem({ ...menuItem, higherCategoryId: selectedCategory?._id || null });
          setItemQuantity(existingItem.quantity);
          setSelectedExtras(existingItem.extras || []);
          setSelectedAttributes(existingItem.selectedAttributes || []);
          setItemComments(existingItem.comments || '');
          setIsItemSelectionModalOpen(true);
          setEditingCartId(cartItemId);
          return;
        }
      }

      // Initialize with defaults for a new item
      setSelectedItem({ ...item, higherCategoryId: selectedCategory?._id || null });
      setItemQuantity(1);
      setSelectedExtras([]);
      setSelectedAttributes(handleUpdateAttributesFromDefaultValue(item) || []);
      setItemComments('');
      setIsItemSelectionModalOpen(true);
      setEditingCartId(null);
    } else {
      orderContext.setLoginModal(true);
      orderContext.setShouldShowReview(true);
    }
  };

  const handleUpdateAttributesFromDefaultValue = (item) => {
    if (!item?.attributes?.length) return [];

    const initialAttributes = [];

    item.attributes.forEach((attribute) => {
      // Skip the attribute entirely if belongsto is true
      if (attribute.belongsto) return;

      if (!attribute.isMultiple || !attribute.options?.length) return;

      const options = [];

      attribute.options.forEach((option) => {
        const rawDefault = option.defaultValue;
        const defaultVal =
          rawDefault !== null && rawDefault !== undefined && rawDefault !== ''
            ? parseInt(rawDefault, 10)
            : 0;

        if (!isNaN(defaultVal) && defaultVal > 0) {
          options.push({
            id: option._id,
            name: option?.name || option?.Name || 'Unknown',
            additionalPrice: option?.additionalPrice || 0,
            price: option?.price || option?.Price || 0,
            quantity: defaultVal,
            isMultiple: attribute?.isMultiple || false,
            isconnectedto: option?.isconnectedto || null,
          });
        }
      });

      if (options.length > 0) {
        initialAttributes.push({
          id: attribute._id,
          attributeId: attribute._id,
          options,
          isMultiple: attribute?.isMultiple || false,
        });
      }
    });

    return initialAttributes.length > 0 ? initialAttributes : [];
  };

  // Check if items have the same configuration
  const haveSameConfiguration = (item1, item2) => {
    // Check comments
    if (item1.comments !== item2.comments) return false;

    // Check extras
    const extras1 = item1.extras || [];
    const extras2 = item2.extras || [];
    if (extras1.length !== extras2.length) return false;

    // Compare each extra
    for (const extra1 of extras1) {
      const matchingExtra = extras2.find(
        (e) => e.id === extra1.id && e.quantity === extra1.quantity,
      );
      if (!matchingExtra) return false;
    }

    // Check attributes
    const attrs1 = item1.selectedAttributes || [];
    const attrs2 = item2.selectedAttributes || [];
    if (attrs1.length !== attrs2.length) return false;

    // Compare each attribute and its options
    for (const attr1 of attrs1) {
      const matchingAttr = attrs2.find((a) => a.attributeId === attr1.attributeId);
      if (!matchingAttr) return false;

      // Compare connectedFromId (for connected attributes)
      const connectedFromId1 = attr1?.connectedFromId?.toString() || '';
      const connectedFromId2 = matchingAttr?.connectedFromId?.toString() || '';
      if (connectedFromId1 !== connectedFromId2) return false;

      const options1 = attr1?.options || [];
      const options2 = matchingAttr?.options || [];
      if (options1?.length !== options2?.length) return false;

      // Compare each option (including quantity)
      for (const opt1 of options1) {
        const matchingOpt = options2?.find(
          (o) => o.id === opt1?.id && (o?.quantity || 1) === (opt1?.quantity || 1),
        );
        if (!matchingOpt) return false;
      }
    }

    return true;
  };

  const validateRequiredAttributes = () => {
    // Skip validation if there's no item selected
    if (!selectedItem) return true;

    // Check each option with minlimit
    const optionsBelowMinLimit = [];
    let totalQuantity = 0;

    // Check minlimit for all attributes
    (selectedItem.attributes || []).forEach((attribute) => {
      // Skip validation for attributes that belong to parent options
      if (attribute?.belongsto) return;

      attribute?.options?.forEach((sourceOption) => {
        // Find the selected quantity for this option
        const attrSelection = selectedAttributes?.find(
          (attr) => attr.attributeId?.toString() === attribute._id?.toString(),
        );

        const selectedOption = attrSelection?.options?.find(
          (opt) => opt.id?.toString() === sourceOption._id?.toString(),
        );

        const selectedQuantity = selectedOption?.quantity || 0;
        totalQuantity += selectedQuantity;

        // Validate: quantity must meet minlimit
        if (sourceOption?.minlimit) {
          if (selectedQuantity < sourceOption.minlimit) {
            optionsBelowMinLimit.push({ ...sourceOption, quantity: selectedQuantity });
          }
        }
      });
    });

    const availableAttribute = selectedItem.attributes?.[0];

    if (
      availableAttribute?.minNumberAllowedTotalItem &&
      totalQuantity < Number(availableAttribute?.minNumberAllowedTotalItem)
    ) {
      setValidationErrorMessage(
        `${translations[language]?.total || 'Total'} ${
          translations[language]?.minimum || 'Minimum'
        }: ${availableAttribute.minNumberAllowedTotalItem} | ${
          translations[language]?.total || 'Total'
        } ${translations[language]?.selected || 'Selected'}: ${totalQuantity || 0}`,
      );

      return false;
    }

    if (optionsBelowMinLimit?.length > 0) {
      const optionNames = optionsBelowMinLimit
        .map(
          (opt) =>
            `${opt.name} (${translations[language]?.minimum || 'Minimum'}: ${opt.minlimit} | ${
              translations[language]?.selected || 'Selected'
            }: ${opt.quantity || 0})`,
        )
        .join('\n');

      if (optionNames) {
        setValidationErrorMessage(
          `${translations[language]?.restaurant_menu_minimum_quantity_message}:\n${optionNames}`,
        );

        return false;
      }

      return false;
    }

    // Get all required attributes
    const requiredAttributes =
      selectedItem.attributes?.filter((attr) => attr.attributesRequired) || [];

    // If no required attributes, validation passes
    if (requiredAttributes.length === 0) return true;

    // Check each required attribute to see if it has a selection
    for (const requiredAttr of requiredAttributes) {
      // Find if this attribute has a selection in selectedAttributes
      const attrSelection = selectedAttributes
        ?.filter((attr) =>
          requiredAttr?.isMultiple
            ? attr?.options.some((option) => option.quantity && option.quantity > 0)
            : attr,
        )
        ?.find((attr) => attr.attributeId?.toString() === requiredAttr._id?.toString());

      // If no selection or empty options array, validation fails
      if (
        !requiredAttr.belongsto &&
        (!attrSelection || !attrSelection?.options || attrSelection?.options.length === 0)
      ) {
        setValidationErrorMessage(
          `${translations[language]?.restaurant_menu_select_option_message} "${requiredAttr.name}"`,
        );
        return false;
      }

      if (!requiredAttr.belongsto) {
        const connectedAttributes = [];

        if (attrSelection) {
          attrSelection?.options.forEach((item) => {
            if (item?.isconnectedto && item?.isconnectedto.length > 0) {
              item?.isconnectedto.forEach((item2) => {
                const itemFind = requiredAttributes?.find((item3) => item3._id === item2);

                if (itemFind) {
                  connectedAttributes.push(itemFind);
                }
              });
            }
          });
        }

        for (const requiredConnectedAttri of connectedAttributes) {
          const connectedAttrSelection = selectedAttributes?.find(
            (attr) => attr.attributeId?.toString() === requiredConnectedAttri._id?.toString(),
          );

          if (
            !connectedAttrSelection ||
            !connectedAttrSelection?.options ||
            connectedAttrSelection?.options.length === 0
          ) {
            setValidationErrorMessage(
              `${translations[language]?.restaurant_menu_select_option_message} "${requiredConnectedAttri.name}"`,
            );
            return false;
          }
        }
      }
    }

    return true;
  };

  const handleAddToOrder = () => {
    // Validate required attributes first
    if (!validateRequiredAttributes()) {
      setIsValidationErrorModalOpen(true);
      return;
    }

    const selectedAttributesArray = selectedAttributes.map((selectedAttr) => {
      const attribute = selectedItem?.attributes?.find(
        (attr) => attr._id?.toString() === selectedAttr?.attributeId?.toString(),
      );

      return {
        id: selectedAttr?.attributeId,
        attributeId: selectedAttr?.attributeId,
        connectedFromId: selectedAttr?.connectedFromId || null,
        attributeName: attribute?.name || translations[language]?.unknown || 'Unknown',
        options: selectedAttr?.options,
        isMultiple: attribute?.isMultiple || false,
      };
    });

    if (editingCartId) {
      // Create the updated item with the same cartId
      const updatedItem = {
        ...selectedItem,
        cartId: editingCartId,
        quantity: itemQuantity,
        extras: selectedExtras,
        selectedAttributes: selectedAttributesArray,
        comments: itemComments,
        totalPrice: calculateModalTotalPrice(),
      };

      // Check if there's already an item with the same configuration (excluding the one being edited)
      const existingItemIndex = orderItems.findIndex(
        (item) =>
          item.cartId !== editingCartId && // Skip the item being edited
          item?._id === selectedItem?._id &&
          haveSameConfiguration(item, updatedItem),
      );

      if (existingItemIndex !== -1) {
        // Remove the item being edited and update the quantity of the existing item
        setOrderItems((prev) =>
          prev
            .filter((item) => item.cartId !== editingCartId) // Remove the item being edited
            .map((item, index) =>
              index === existingItemIndex
                ? {
                    ...item,
                    quantity: item.quantity + itemQuantity,
                    totalPrice: calculateItemTotal(item, item.quantity + itemQuantity),
                  }
                : item,
            ),
        );
      } else {
        // Replace the old item with the updated one
        setOrderItems((prev) =>
          prev.map((item) => (item.cartId === editingCartId ? updatedItem : item)),
        );
      }
    } else {
      const tempItem = {
        _id: selectedItem._id,
        extras: selectedExtras,
        selectedAttributes: selectedAttributesArray,
        comments: itemComments,
      };

      // Check if there's already an item with the same configuration
      const existingItemIndex = orderItems.findIndex(
        (item) => item?._id === selectedItem?._id && haveSameConfiguration(item, tempItem),
      );

      if (existingItemIndex !== -1) {
        // Update the quantity of the existing item instead of adding a new one
        setOrderItems((prev) =>
          prev.map((item, index) =>
            index === existingItemIndex
              ? {
                  ...item,
                  quantity: item.quantity + itemQuantity,
                  totalPrice: calculateItemTotal(item, item.quantity + itemQuantity),
                }
              : item,
          ),
        );
      } else {
        // Add as a new item
        const itemToAdd = {
          ...selectedItem,
          cartId: `${selectedItem._id}-${Date.now()}`,
          quantity: itemQuantity,
          extras: selectedExtras,
          selectedAttributes: selectedAttributesArray,
          comments: itemComments,
          totalPrice: calculateModalTotalPrice(),
        };

        setOrderItems((prev) => [...prev, itemToAdd]);
      }
    }

    // Reset the editing state and close the modal
    setEditingCartId(null);
    setIsItemSelectionModalOpen(false);
  };

  const editCartItem = (item) => {
    handleItemSelectionModalOpen(item, item.cartId);
  };

  const handleRadioAttributeChange = (attributeId, selectedOption, connectedFromId) => {
    setSelectedAttributes((prev) => {
      // Find the current selection for this attribute (if any)
      const existingAttribute = prev.find((attr) => attr.attributeId === attributeId);
      const previousOptionId = existingAttribute?.options?.[0]?.id?.toString() || '';

      // Remove any existing selection for this attribute
      let filtered = prev.filter((attr) => attr.attributeId !== attributeId);

      // If there was a previous selection and it's different from the new one,
      // remove any attributes connected to the old option
      if (previousOptionId && previousOptionId !== (selectedOption?.id?.toString() || '')) {
        filtered = filtered.filter((attr) => {
          // Check if this attribute has any options connected to the previous option
          const hasConnectionToPreviousOption = attr.options?.some(
            (opt) => opt?.isconnectedto?.toString() === previousOptionId,
          );
          const connectedFromIdStr = attr.connectedFromId?.toString();

          // Keep the attribute only if it doesn't have connections to the previous option
          return connectedFromIdStr !== previousOptionId && !hasConnectionToPreviousOption;
        });
      }

      // If no option is selected, just return the filtered array
      if (!selectedOption) {
        return filtered;
      }

      // Add the new selection
      return [
        ...filtered,
        {
          id: attributeId,
          attributeId: attributeId,
          connectedFromId,
          options: [selectedOption],
        },
      ];
    });
  };

  const handleMultipleAttributeChange = (attributeId, isSelected, option) => {
    setSelectedAttributes((prev) => {
      // Find if we already have this attribute in our array
      const existingAttributeIndex = prev.findIndex((attr) => attr.attributeId === attributeId);

      if (existingAttributeIndex === -1 && isSelected) {
        // If attribute doesn't exist and option is selected, create a new entry
        return [
          ...prev,
          {
            id: attributeId,
            attributeId,
            options: [option],
          },
        ];
      } else if (existingAttributeIndex !== -1) {
        // Attribute exists, update its options
        const updatedAttributes = [...prev];
        const attribute = selectedItem?.attributes?.find(
          (attr) => attr._id?.toString() === attributeId?.toString(),
        );

        if (isSelected) {
          // Check if the attribute allows multiple selections
          if (attribute?.isMultiple) {
            // Add option to existing options
            updatedAttributes[existingAttributeIndex] = {
              ...updatedAttributes[existingAttributeIndex],
              options: [...updatedAttributes[existingAttributeIndex].options, option],
            };
          } else {
            // Replace existing options for non-multiple attributes
            updatedAttributes[existingAttributeIndex] = {
              ...updatedAttributes[existingAttributeIndex],
              options: [option],
            };
          }
        } else {
          // Remove option from options
          updatedAttributes[existingAttributeIndex] = {
            ...updatedAttributes[existingAttributeIndex],
            options: updatedAttributes[existingAttributeIndex].options.filter(
              (opt) => opt.id !== option.id,
            ),
          };

          // If no options left, remove the attribute entry
          if (updatedAttributes[existingAttributeIndex].options.length === 0) {
            updatedAttributes.splice(existingAttributeIndex, 1);
          }
        }

        return updatedAttributes;
      }

      return prev;
    });
  };

  const handleMultipleCounterChange = (attributeId, itemId, newCounts) => {
    setSelectedAttributes((prev) => {
      // Find if we already have this attribute in our array
      const existingAttributeIndex = prev.findIndex((attr) => attr.attributeId === attributeId);

      // Find the attribute from the selected item
      const attribute = selectedItem?.attributes?.find(
        (attr) => attr._id?.toString() === attributeId?.toString(),
      );

      if (existingAttributeIndex === -1) {
        // If attribute doesn't exist and at least one option has a count > 0, create a new entry
        const options = [];

        // Process all counts and create options for those with count > 0
        Object.entries(newCounts).forEach(([optionId, count]) => {
          if (count > 0) {
            const option = attribute?.options?.find(
              (opt) => opt._id?.toString() === optionId?.toString(),
            );

            if (option) {
              options.push({
                id: option._id,
                name: option?.name || translations[language]?.unknown || 'Unknown',
                additionalPrice: option?.additionalPrice || 0,
                price: option?.price || 0,
                quantity: count,
                isMultiple: attribute?.isMultiple || false,
                isconnectedto: option?.isconnectedto || null,
              });
            }
          }
        });

        // Only add the attribute if there are options with counts
        if (options.length > 0) {
          return [
            ...prev,
            {
              id: attributeId,
              attributeId,
              options,
              isMultiple: attribute?.isMultiple || false,
            },
          ];
        }
      } else {
        // Attribute exists, update its options
        const updatedAttributes = [...prev];

        // Create new options array based on the new counts
        const options = [];
        Object.entries(newCounts).forEach(([optionId, count]) => {
          // Only include options with count > 0
          if (count > 0) {
            const option = attribute?.options?.find(
              (opt) => opt._id?.toString() === optionId?.toString(),
            );

            if (option) {
              options.push({
                id: option._id,
                name: option?.name || translations[language]?.unknown || 'Unknown',
                additionalPrice: option?.additionalPrice || 0,
                price: option?.price || 0,
                quantity: count,
                isMultiple: attribute?.isMultiple || false,
                isconnectedto: option?.isconnectedto || null,
              });
            }
          }
        });

        // Calculate total quantity for the current attribute from newCounts
        const currentAttributeQuantity = newCounts[itemId];

        // If current attribute's quantity is 0, remove the attribute entry
        if (currentAttributeQuantity === 0) {
          const currentItemIdStr = itemId?.toString();

          // Remove attributes that have options connected to the removed option
          const filteredAttributes = updatedAttributes.filter((attr) => {
            // Check if this attribute has any options connected to the removed option
            const hasConnectionToRemovedItem = attr.options?.some(
              (opt) => opt?.isconnectedto?.toString() === currentItemIdStr,
            );
            const connectedFromIdStr = attr.connectedFromId?.toString();

            // Keep the attribute only if it doesn't have connections to the removed item
            return currentItemIdStr !== connectedFromIdStr && !hasConnectionToRemovedItem;
          });

          // Update the current attribute with remaining options
          if (options.length > 0) {
            filteredAttributes[existingAttributeIndex] = {
              ...updatedAttributes[existingAttributeIndex],
              options,
            };
            return filteredAttributes;
          } else {
            // If no options left, also remove the current attribute
            return filteredAttributes.filter(
              (attr) => attr.attributeId?.toString() !== attributeId?.toString(),
            );
          }
        } else {
          // Otherwise update with the new options
          updatedAttributes[existingAttributeIndex] = {
            ...updatedAttributes[existingAttributeIndex],
            options,
          };
        }

        return updatedAttributes;
      }

      return prev;
    });
  };

  const handleExtrasChange = (newCounts) => {
    // Convert the counts object to an array of extras
    const extrasArray = Object.entries(newCounts)
      .filter(([_, count]) => count > 0)
      .map(([id, count]) => {
        const extra = selectedItem?.extras?.find((extra) => extra._id === id);
        return {
          id,
          name: extra?.name || translations[language]?.unknown || 'Unknown',
          price: extra?.price || 0,
          quantity: count,
        };
      });

    setSelectedExtras(extrasArray);
  };

  const handleRemoveFromOrder = (cartId) => {
    setOrderItems((prev) => prev.filter((item) => item.cartId !== cartId));

    // If cart is empty, reset additional options
    if (orderItems.length <= 1) {
      setHasBag(false);
      setHasDeliveryFee(false);
      setComments('');
      navigateToStep('menu');
    }
  };

  const handleUpdateItemQuantity = (cartId, newQuantity) => {
    if (newQuantity <= 0) {
      handleRemoveFromOrder(cartId);
      return;
    }

    setOrderItems((prev) =>
      prev.map((item) =>
        item.cartId === cartId
          ? { ...item, quantity: newQuantity, totalPrice: calculateItemTotal(item, newQuantity) }
          : item,
      ),
    );
  };

  const calculateModalTotalPrice = () => {
    let total = selectedItem?.price || 0;

    // Add extras
    selectedExtras?.forEach((extra) => {
      total += Number(extra?.price || 0) * Number(extra?.quantity || 1);
    });

    // Add attributes price
    selectedAttributes?.forEach((attribute) => {
      if (attribute?.isMultiple) {
        attribute?.options?.forEach((option) => {
          if (option && (option?.Price || option?.additionalPrice)) {
            if (attribute?.connectedFromId) {
              // Find the parent option this attribute is connected from
              const parentOption = selectedAttributes
                ?.flatMap((attr) => attr?.options || [])
                ?.find((opt) => opt?.id === attribute?.connectedFromId);

              total += Number(option?.Price || 0) * Number(parentOption?.quantity || 1);
            } else {
              total += Number(option?.additionalPrice || 0) * Number(option?.quantity || 1);
            }
          }
        });

        return;
      }

      attribute?.options?.forEach((option) => {
        if (option && (option?.Price || option?.additionalPrice)) {
          if (attribute?.connectedFromId) {
            // Find the parent option this attribute is connected from
            const parentOption = selectedAttributes
              ?.flatMap((attr) => attr?.options || [])
              ?.find((opt) => opt?.id === attribute?.connectedFromId);

            total += Number(option?.Price || 0) * Number(parentOption?.quantity || 1);
          } else {
            total += Number(option?.additionalPrice || 0);
          }
        }
      });
    });

    return total * itemQuantity;
  };

  const calculateItemTotal = (item, quantity) => {
    let price = item?.price || 0;

    // Add extras
    item?.extras?.forEach((extra) => {
      price += Number(extra?.price || 0) * Number(extra?.quantity || 1);
    });

    // Add selected attributes
    item?.selectedAttributes?.forEach((attr) => {
      if (attr?.isMultiple) {
        attr?.options?.forEach((option) => {
          if (option && (option?.Price || option?.additionalPrice)) {
            if (attr?.connectedFromId) {
              // Find the parent option this attribute is connected from
              const parentOption = item?.selectedAttributes
                ?.flatMap((a) => a?.options || [])
                ?.find((opt) => opt?.id === attr?.connectedFromId);

              price += Number(option?.Price || 0) * Number(parentOption?.quantity || 1);
            } else {
              price += Number(option?.additionalPrice || 0) * Number(option?.quantity || 1);
            }
          }
        });

        return;
      }

      attr?.options?.forEach((option) => {
        if (option && (option?.Price || option?.additionalPrice)) {
          if (attr?.connectedFromId) {
            // Find the parent option this attribute is connected from
            const parentOption = item?.selectedAttributes
              ?.flatMap((a) => a?.options || [])
              ?.find((opt) => opt?.id === attr?.connectedFromId);

            price += Number(option?.Price || 0) * Number(parentOption?.quantity || 1);
          } else {
            price += Number(option?.additionalPrice || 0);
          }
        }
      });
    });

    return price * quantity;
  };

  const calculateTotal = (
    excludeTip = false,
    excludeBagFee = false,
    excludeDeliveryFee = false,
    excludeWoltDeliveryFee = false,
    excludeDiscountCode = false,
  ) => {
    let total = orderItems.reduce(
      (sum, item) => sum + (item.totalPrice || calculateItemTotal(item, item.quantity)),
      0,
    );

    // Guest discount code — off the ITEM SUBTOTAL, before tip / bag / delivery /
    // Wolt, so the tip percentage and the service fee are both derived from the
    // reduced amount, mirroring how the backend prices the order.
    // discountCodeAmount is SERVER-COMPUTED; it is never derived here.
    //
    // excludeDiscountCode serves only the struck-through "before" figure in
    // YourItems — the code also lowers the tip and service-fee base, so that
    // figure cannot be recovered by adding the amount back on.
    if (!excludeDiscountCode) {
      total = Math.max(
        0,
        total - (discountApplied?.valid ? Number(discountApplied.amount) || 0 : 0),
      );
    }

    // Add tip if not excluded
    if (!excludeTip && (tipPercentage?.percentage || 0) > 0) {
      total += parseFloat(tipPercentage?.percentage || 0) * total;
    }

    // Add take-away bag fee if applicable
    if (
      !excludeBagFee &&
      (orderType === 'take-away' || orderType === 'delivery') &&
      hasBag &&
      bagFee
    ) {
      total += parseFloat(bagFee);
    }

    // Add delivery fee if applicable
    if (!excludeDeliveryFee && orderType === 'take-away' && hasDeliveryFee && deliveryFee) {
      total += parseFloat(deliveryFee);
    }

    // Add wolt delivery fee if applicable
    if (
      !excludeWoltDeliveryFee &&
      orderType === 'delivery' &&
      restaurant?.EnableWoltDelivery &&
      woltDeliveryFee
    ) {
      total += parseFloat(woltDeliveryFee);
    }

    return total;
  };

  const grandTotal = (excludeDiscountCode = false) => {
    let total = calculateTotal(false, false, false, false, excludeDiscountCode);

    if (hasServiceFee && serviceFee && !hasFlatFee && !flatFee) {
      total += total * parseFloat(serviceFee / 100);
    } else if (hasServiceFee && serviceFee && hasFlatFee && flatFee) {
      total += flatFee && total ? flatFee : 0;
      total += total * parseFloat(serviceFee / 100);
    } else {
      total += flatFee && total ? flatFee : 0;
    }

    return total;
  };

  const calculatedServiceFee = () => {
    let total = calculateTotal();

    if (hasServiceFee && serviceFee && !hasFlatFee && !flatFee) {
      let calculatedServiceFee = total * parseFloat(serviceFee / 100);

      return calculatedServiceFee;
    } else if (hasServiceFee && serviceFee && hasFlatFee && flatFee) {
      total += flatFee && total ? flatFee : 0;
      let calculatedServiceFee = total * parseFloat(serviceFee / 100);

      return calculatedServiceFee;
    }

    return null;
  };

  const handleBagToggle = useCallback(() => {
    setHasBag(!hasBag);
  }, [hasBag]);

  const handleDeliveryFeeToggle = useCallback(() => {
    setHasDeliveryFee(!hasDeliveryFee);
  }, [hasDeliveryFee]);

  const handleWoltDeliveryFeeToggle = useCallback(() => {
    if (!isWoltDeliveryEnabled) {
      setWoltDelivery(null);
    }

    setIsWoltDeliveryEnabled(!isWoltDeliveryEnabled);
  }, [isWoltDeliveryEnabled]);

  const handleOrderTypeChange = (type) => {
    if (toggleOrderIfNotAllowed(type)) {
      setOrderNotAllowed(true);
      return;
    }

    setOrderType(type);

    if (restaurant?.allowAdvanceOrder) {
      setPickup('later');
    }
  };

  const handleCommentsChange = (newComments) => {
    setComments(newComments);
  };

  const handleViewCart = () => {
    if (restaurant.orderingPaused) {
      showOrderingPaused();
      return;
    }

    if (!restaurant.isMenuBuyable) {
      return;
    }

    if (!restaurant.isdinein && !restaurant.istakeout) {
      return;
    }

    if (toggleOrderIfNotAllowed()) {
      setOrderNotAllowed(true);
      return;
    }

    if (checkIfCategoryIsDisabled()) {
      setMenuNotAvailable(true);
      return;
    }

    if (isUserAllowedToOrder) {
      if (orderItems.length === 0) {
        alert(`${translations[language]?.restaurant_menu_empty_cart}!`);
        return;
      }

      // Calculate initial tip amount
      const initialTotal = calculateTotal(
        true,
        hasBag,
        hasDeliveryFee,
        restaurant?.EnableWoltDelivery,
      );
      setTipAmount(parseFloat(tipPercentage?.percentage || 0) * initialTotal);

      // Navigate to review step
      navigateToStep('review');
    } else {
      orderContext.setLoginModal(true);
      orderContext.setShouldShowReview(true);
    }
  };

  // Calculate tip amount when tipPercentage or total changes
  useEffect(() => {
    const orderTotal = calculateTotal(true, hasBag, hasDeliveryFee, restaurant?.EnableWoltDelivery);
    setTipAmount(parseFloat(tipPercentage?.percentage || 0) * orderTotal);

    // Set tipId based on percentage for backend
    if (parseFloat(tipPercentage?.percentage || 0) === 0) {
      setTipId(null);
    } else {
      // This would normally come from your tip options data
      // Just setting a placeholder ID for now based on percentage
      setTipId(tipPercentage?.value);
    }
    // calculateTotal is a plain function, so the discount amount has to be a
    // dependency of its own or the tip is left computed on the pre-discount base.
  }, [tipPercentage, orderItems, hasBag, hasDeliveryFee, discountApplied.amount]);

  useEffect(() => {
    if (!lagTime) return;

    const now = new Date();
    const earliestAvailable = new Date(now.getTime() + lagTime * 60000);

    // Round up to nearest lag time
    earliestAvailable.setMinutes(
      Math.ceil(earliestAvailable.getMinutes() / lagTime) * lagTime,
      0,
      0,
    );

    // If earliest available time is tomorrow, set date to tomorrow
    if (earliestAvailable.getDate() !== now.getDate()) {
      const tomorrow = new Date(now);
      tomorrow.setDate(tomorrow.getDate() + 1);
      tomorrow.setHours(0, 0, 0, 0);
      setPickupDate(tomorrow);
      setIsPickupForTomorrow(tomorrow);
    }
  }, [lagTime]);

  useEffect(() => {
    if (!restaurant || !pickupDate) return;

    // For today, consider current time + lagTime
    const now = new Date(); // current date & time
    const earliestAvailable = new Date(now.getTime() + lagTime * 60000);

    // Round up to nearest lag time
    earliestAvailable.setMinutes(
      Math.ceil(earliestAvailable.getMinutes() / pickupTimeInterval) * pickupTimeInterval,
      0,
      0,
    );

    // Format as HH:mm
    const timeString = moment(earliestAvailable).format('HH:mm');

    // Compare with restaurant's earliest available time
    const restaurantEarliestTime = getEarliestTime(
      restaurant,
      lagTime,
      pickupTimeInterval,
      pickupDate,
      selectedCategory,
    );

    // Use the later of the two times
    if (timeString > restaurantEarliestTime) {
      setPickupTime(timeString);
    } else {
      setPickupTime(restaurantEarliestTime);
    }
  }, [restaurant, pickupDate, lagTime]);

  // Prepare extras items for the counter component
  const extrasItems =
    selectedItem?.extras?.map((extra) => ({
      id: extra._id,
      label: extra.name,
      price: extra.price,
      initialCount: 0,
    })) || [];

  // Group attributes by their type
  const groupedAttributes =
    selectedItem?.attributes?.reduce((acc, attribute) => {
      // Skip attributes that belong to specific options
      if (attribute.belongsto) return acc;

      // Add id property that is the same as _id
      const attributeWithId = {
        ...attribute,
        id: attribute._id,
      };

      // Convert options to the format needed by our UI components
      const options = attribute.options?.map((option) => ({
        ...option,
        id: option._id,
        name: option.name,
        additionalPrice: option.additionalPrice,
        price: option.additionalPrice || 0,
        priceLabel: option.additionalPrice
          ? `${restaurant?.currency?.code || 'DKK'} ${option.additionalPrice}`
          : '',
        isconnectedto: option?.isconnectedto || null,
      }));

      if (options && options.length > 0) {
        acc.push({
          ...attributeWithId,
          options,
        });
      }

      return acc;
    }, []) || [];

  // Modal Footer Component
  const ItemSelectionFooter = useCallback(() => {
    return (
      <div className="bg-white !p-3 sm:!p-6 border-t-[1px] border-[#f5f5f5] flex flex-col sm:flex-row items-center justify-between rounded-b-2xl gap-3 sm:gap-0">
        <div className="w-full max-w-full sm:max-w-[250px] flex items-center justify-between">
          <Counter
            className="p-2 border border-[#f3f3f3] rounded-[50px]"
            initialValue={itemQuantity}
            value={itemQuantity}
            onChange={setItemQuantity}
          />
          <p className="font-bold text-sm sm:text-base">
            {restaurant?.currency?.code || 'DKK'} {calculateModalTotalPrice().toFixed(2)}
          </p>
        </div>
        <button
          className={`w-full sm:w-auto bg-primary text-xs sm:text-sm text-white font-bold !px-2 py-[10px] sm:!px-4 sm:py-[15px] rounded-full hover:bg-secondary transition-colors ${
            itemQuantity <= 0 && 'bg-gray-300 hover:bg-gray-400 cursor-not-allowed'
          }`}
          onClick={handleAddToOrder}
          disabled={itemQuantity <= 0}
        >
          <i className="fa fa-plus mr-2" style={{ fontSize: 12 }} />
          {editingCartId
            ? translations[language]?.restaurant_menu_order_item_edit
            : translations[language]?.restaurant_menu_order_item_add}
        </button>
      </div>
    );
  }, [
    itemQuantity,
    restaurant?.currency?.code,
    calculateModalTotalPrice,
    handleAddToOrder,
    editingCartId,
    translations,
    language,
  ]);

  // Start - OrderInformation Functions
  const handleTableNumberChange = useCallback((value) => {
    setTableNumber(value);
  }, []);

  const handleTipPercentageChange = useCallback((value) => {
    setTipPercentage(value);
  }, []);

  const handlePhoneNumberChange = useCallback((value) => {
    setPhoneNumber(value);
  }, []);

  const handleDiscountCodeChange = useCallback((value) => {
    setDiscountCode(value);
    // Editing the code invalidates the previous quote immediately — never leave
    // the old code's amount on the total while a new one is being typed. Idle,
    // not checking: 'checking' shows the spinner and an in-flight Apply locks the
    // field, and a kiosk on-screen keyboard is the last place to steal focus.
    setDiscountApplied(EMPTY_DISCOUNT);
    setDiscountError('');
    setDiscountStatus('idle');
  }, []);

  // Apply / Enter: tells the lookup to stop waiting out its debounce.
  const handleApplyDiscount = useCallback(() => {
    setDiscountApplyNonce((nonce) => nonce + 1);
  }, []);

  const handleRemoveDiscount = useCallback(() => {
    setDiscountCode('');
    setDiscountApplied(EMPTY_DISCOUNT);
    setDiscountError('');
    setDiscountStatus('idle');
  }, []);

  // The only place a discount amount enters this page: the answer from
  // /discountcode/validate. It always reports — including a zero — so a code
  // that stopped applying can never leave a stale amount on the kiosk screen.
  const handleDiscountResult = useCallback((result) => {
    // An idle report means "the field is empty, there is nothing to check". It
    // must not wipe an order-level rejection: handleDiscountCodeRejected empties
    // the field on purpose and leaves the reason on screen for the guest to read.
    if (result?.status === 'idle') {
      setDiscountApplied(EMPTY_DISCOUNT);
      setDiscountStatus((current) =>
        current === 'error' || current === 'unsupported' ? current : 'idle',
      );
      return;
    }

    setDiscountStatus(result?.status || 'idle');
    setDiscountError(result?.valid ? '' : result?.message || '');
    setDiscountApplied(
      result?.valid
        ? {
            valid: true,
            code: result.code,
            amount: Number(result.amount) || 0,
            type: result.type || null,
            value: Number(result.value) || 0,
          }
        : EMPTY_DISCOUNT,
    );
  }, []);

  // The order was refused because of the code. Clear it, show the reason and put
  // the guest back on the review step. Never retry, never resubmit.
  const handleDiscountCodeRejected = useCallback(
    (discountCodeError, message) => {
      setDiscountCode('');
      setDiscountApplied(EMPTY_DISCOUNT);
      setDiscountStatus('error');
      setDiscountError(
        discountMessage(language, discountCodeError?.messageKey, discountCodeError?.meta, message),
      );
      navigateToStep('review');
    },
    // navigateToStep is a stable page helper declared above; language is the
    // only value that can change the copy.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [language],
  );

  // A code belongs to exactly one restaurant, and a code with no cart has
  // nothing to discount. A kiosk runs unattended between guests, so leaving
  // either behind would hand the next guest somebody else's discount.
  const discountRestaurantRef = useRef(restaurantId);
  useEffect(() => {
    if (discountRestaurantRef.current === restaurantId) return;
    discountRestaurantRef.current = restaurantId;
    handleRemoveDiscount();
  }, [restaurantId, handleRemoveDiscount]);

  useEffect(() => {
    if (orderItems.length === 0 && discountCode) handleRemoveDiscount();
  }, [orderItems.length, discountCode, handleRemoveDiscount]);

  const handlePickupChange = (pickup) => {
    if (toggleOrderIfNotAllowed(null, pickup)) {
      setOrderNotAllowed(true);
      return;
    }

    if (pickup == 'now') {
      setPickup('now');
    } else {
      if (!restaurant.allowAdvanceOrder) {
        return;
      }

      setPickup('later');
    }
  };

  const handlePickupDateChange = useCallback((date) => {
    setPickupDate(date);
  }, []);

  const handlePickupTimeChange = useCallback((time) => {
    setPickupTime(time);
  }, []);

  const handleRoomNumberChange = useCallback((roomNumber) => {
    setRoomNumber(roomNumber);
  }, []);
  // End - OrderInformation Functions

  // Start - Navigation for OrderInformation & YourItems
  const handleNextStep = async () => {
    // Validate the form fields
    let isOrderInfoValid = orderInfoRef.current.validateForm();
    let isGuestInfoValid = true;

    let timeValidationResult = { isValid: true };
    if (yourItemsRef.current) {
      timeValidationResult = yourItemsRef.current.validateTimeAvailability();
    }

    if (isGuest) {
      isGuestInfoValid = guestInfoRef.current.validateForm();
    }

    if (!timeValidationResult.isValid) {
      setValidationErrorMessage(
        timeValidationResult.errorMessage || 'Some items are not available at the selected time',
      );
      setIsValidationErrorModalOpen(true);
      return;
    }

    // A discount check in flight holds EMPTY_DISCOUNT, so leaving review now
    // would send the order with no code at all and charge full price without
    // saying so. Wait for the quote to settle instead.
    if (discountStatus === 'checking') return;

    // Only proceed to next step if validation passes
    if (isOrderInfoValid && isGuestInfoValid && timeValidationResult.isValid) {
      if (currentStep === 'review') {
        navigateToStep('payment');

        if (isGuest) {
          await registerGuestUser();
        }
      } else if (currentStep === 'payment') {
        handlePayment();
      }
    }
  };

  const handlePrevStep = () => {
    if (currentStep === 'review') {
      navigateToStep('menu');
    } else if (currentStep === 'payment') {
      navigateToStep('review');
    }
  };

  const handlePayment = () => {
    if (orderItems.length === 0) {
      alert(`${translations[language]?.restaurant_menu_empty_cart}!`);
      return;
    }

    setIsCheckoutModalOpen(false);
  };

  const registerGuestUser = async () => {
    const payload = {
      firstname: guestInfo.name || '',
      lastname: '',
      phone: phoneNumber || '',
      email: guestInfo.email || '',
    };

    const response = await axios({
      method: 'POST',
      url: `${configData.SERVER_URL}/orders/guest/createuser`,
      data: payload,
    });

    // Update the guest id to be from the db id
    orderContext.setGuestId(response.data._id.toString());
    orderContext.setIsGuest(true);
    localStorage.setItem('guest-uui', response.data._id.toString());

    return response.data;
  };

  const handleGoToSelectPaymentMethod = () => {
    paymentRef.current?.scrollIntoView({ behavior: 'smooth' });
  };
  // End - Navigation for OrderInformation & YourItems

  // Start - Payment Processing Functions
  const handleProcessPayment = async (paymentMethod, orderDetails) => {
    setLoading(true);

    try {
      const isDineIn = orderType === 'dine-in';
      const isAdvanceOrder = !isDineIn && pickup === 'later';

      // If order details are provided, it means the payment has already been processed
      if (orderDetails) {
        setPayConfirm(orderDetails);
        navigateToStep('menu');
        setLoading(false);

        if (!orderDetails.status) {
          startPendingOrderPolling(orderDetails);
        } else if (orderDetails.status === 'confirmed') {
          startConfirmedOrderPolling(orderDetails);
        }

        return;
      }

      // Check if this is a ParkTable payment
      if (paymentMethod === 'parktable') {
        // Format orders for the API
        const formattedOrders = formatOrdersForCheckout(orderItems);

        const currentDate = moment(pickupDate).format('MM/DD/YYYY');
        const currentTime = moment(pickupTime, 'HH:mm').format('h:mm A');
        const formattedDateTime = moment(currentDate + ' ' + currentTime).format();

        const orderPayload = {
          restaurantId: restaurant._id,
          date: new Date(),
          isDineIn: isDineIn,
          mobilenumber: phoneNumber,
          notes: comments,
          tableNumber: tableNumber ? tableNumber?.value : '',
          promoCodeId: '',
          // Guest discount code. The code STRING and nothing else — the server
          // re-validates it, computes the amount and reserves the redemption.
          // Never overloaded onto promoCodeId. Omitted when no code is applied.
          discountCode: discountApplied.valid ? discountApplied.code : undefined,
          payment_method: paymentMethod,
          tipId: tipId,
          isweb: true,
          iskiosk: true,
          isAdvanceOrder: isAdvanceOrder,
          advanceOrderDate: isAdvanceOrder ? formattedDateTime : undefined,
          order: formattedOrders,
          bagtakeawayprice:
            restaurant?.BagTakeAway && !isDineIn && hasBag ? restaurant?.BagTakeAwayPrice || 0 : 0,
          deliveryfee:
            restaurant?.HasDeliveryFee && !isDineIn && hasDeliveryFee
              ? restaurant?.DeliveryFee || 0
              : 0,
          isParkTable: paymentMethod === 'parktable',
          takeAwayRoom: restaurant?.AskTakeAwayRoom && !isDineIn ? roomNumber : '',
          isGuest: isGuest,
          guestId: orderContext.guestId || '',
          woltShipment: restaurant?.EnableWoltDelivery && woltDelivery ? woltDelivery : null,
          deliveryComment: restaurant?.EnableWoltDelivery && woltDelivery ? comments : '',
          source: 'kiosk',
        };

        let url = `${configData.SERVER_URL}/orders/v2/preorder`;

        const response = await axios({
          method: 'POST',
          url,
          data: orderPayload,
          headers: {
            Authorization: `Bearer ${userProfile?.token || ''}`,
          },
        });

        setLoading(false);
        // Clear cart
        resetOrderInfo();

        // Save order info to cache for status tracking
        if (response.data) {
          if (response.data?.order_details) {
            saveOrderInfoToCache(response.data.order_details);
          }
        }
      }
    } catch (error) {
      setLoading(false);

      // Ordering paused after the page loaded: the server's reason, in the
      // error modal rather than an alert.
      if (subscriptionBlockedFromError(error)) {
        showOrderingPaused(serverMessage(error));
        return;
      }

      // Checked before the 429 branch: `rate_limited` is also a 429, and
      // treating it as a capacity problem would open the wrong modal.
      const discountCodeError = error?.response?.data?.discountCodeError;
      if (discountCodeError) {
        handleDiscountCodeRejected(discountCodeError, error?.response?.data?.Message);
        return;
      }

      if (error?.response?.status === 429) {
        setCapacityFullModal({
          open: true,
          nextSlot: error?.response?.data?.nextAvailableSlot || null,
        });
        return;
      }
      console.error('Payment processing error:', error);
      // Reads the response body, not the AxiosError — see utils/serverError.js.
      // A kiosk guest is standing at the machine with no support line, so a
      // useful sentence matters more here than anywhere else.
      alert(guestFacingError(error));
    }
  };

  const saveOrderInfoToCache = (orderDetails) => {
    localStorage.setItem('hw_pending_orderv2', JSON.stringify(orderDetails));

    setPayConfirm(orderDetails);
    navigateToStep('menu');
    setLoading(false);

    if (!orderDetails.status) {
      startPendingOrderPolling(orderDetails);
    } else if (orderDetails.status === 'confirmed') {
      startConfirmedOrderPolling(orderDetails);
    }
  };

  const handlePersistOrderData = (guestId) => {
    const data = {
      orderType,
      pickup,
      pickupDate,
      pickupTime,
      phoneNumber,
      tableNumber,
      comments,
      tipId,
      tipPercentage,
      restaurantId: restaurant?._id,
      hasBag,
      hasDeliveryFee,
      roomNumber,
      orderItems,
      currentStep,
      guestInfo,
      guestId,
      // Only the code STRING is persisted — never the amount. It is re-validated
      // on restore, so a code that ran out in the meantime cannot come back as a
      // discount that no longer exists.
      discountCode: discountApplied.valid ? discountApplied.code : '',
    };

    persistOrderData(data);
  };

  const resetOrderInfo = () => {
    setOrderItems([]);
    localStorage.removeItem('hw_cart');
    setComments('');
    navigateToStep('menu');
    setTipPercentage(null);
    setPhoneNumber('');
    setTableNumber(null);
    setWoltDelivery(null);
    setWoltDeliveryFee('');
  };

  const addNewCard = async () => {
    setLoading(true);

    // Persist current order state
    handlePersistOrderData(orderContext.guestId);

    try {
      let root_url = window.location.origin
        ? window.location.origin + '/'
        : window.location.protocol + '//' + window.location.host + '/';

      const response = await axios({
        method: 'GET',
        url: `${configData.SERVER_URL}/order/addcard?successuri=${root_url}api/redirect?persistData=1&failuri=${root_url}api/redirect?persistData=1&restaurant=${restaurant?._id}`,
        headers: {
          Authorization: `Bearer ${userProfile?.token}`,
        },
      });

      // Redirect to card addition page
      window.location.replace(response.data);
    } catch (error) {
      console.error('Error adding new card:', error);
      setLoading(false);
    }
  };

  const formatOrdersForCheckout = (items) => {
    return items.map((item) => {
      // Process extras
      const extras = (item.extras || [])
        .filter((extra) => extra.quantity > 0)
        .map((extra) => ({
          _id: extra.id,
          qty: extra.quantity,
        }));

      // Process attributes
      const attributes = [];
      if (item.selectedAttributes && item.selectedAttributes.length > 0) {
        item.selectedAttributes.forEach((attr) => {
          if (attr.options && attr.options.length > 0) {
            attr.options.forEach((option) => {
              attributes.push({
                _id: attr.attributeId,
                value: option.id,
                qty: option.quantity || 1,
                isMultiple: attr?.isMultiple || false,
              });
            });
          }
        });
      }

      return {
        _id: item._id,
        qty: item.quantity,
        extras: extras,
        attributes: attributes,
        notes: item.comments || '',
      };
    });
  };
  // End - Payment Processing Functions

  const handleViewOpeningTimesOpen = useCallback(() => {
    setIsViewOpeningTimesOpen(true);
  }, []);

  const checkIfCategoryIsDisabled = () => {
    if (orderType === 'take-away' && restaurant.allowAdvanceOrder) {
      return false;
    }

    if (isEmpty(selectedCategory)) {
      return !restaurant.isOpen;
    }

    if (orderType !== 'dine-in' && pickup === 'later') {
      return !selectedCategory.isAvailable;
    }

    return !selectedCategory.isAvailable || !restaurant.isOpen;
  };

  const isOpenThisTime = useCallback(() => {
    if (!restaurant) return false;

    const pureTime = currentTime.format('HH:mm');
    const selectedDate = currentTime.format('MM/DD/YYYY');
    const dateTime = moment(selectedDate + ' ' + pureTime, 'MM/DD/YYYY HH:mm');

    return getRestaurantStatus(restaurant, true, dateTime) && restaurant.isOpen;
  }, [currentTime, restaurant]);

  const toggleOrderIfNotAllowed = (updatedOrderType, updatedPickup) => {
    let newType = orderType;
    let newPickup = pickup;

    if (updatedOrderType) {
      newType = updatedOrderType;
    }

    if (updatedPickup) {
      newPickup = updatedPickup;
    }

    if (!isOpenThisTime()) {
      if (newType === 'take-away' && !restaurant.allowAdvanceOrder) {
        return true;
      }

      if (newType === 'dine-in') {
        return true;
      }

      if (newPickup === 'now') {
        return true;
      }
    }

    return false;
  };

  const toggleRestaurantTakeAwayUnavailable = () => {
    if (!restaurant?.istakeout) {
      return true;
    }

    return false;
  };

  const handleOrderTypeSelectionContinue = useCallback(
    (type) => {
      if (restaurant?.orderingPaused) {
        showOrderingPaused();
        return;
      }

      if (toggleOrderIfNotAllowed(type)) {
        setOrderNotAllowed(true);
        return;
      }

      setOrderType(type);
      navigateToStep('menu');

      if (restaurant?.allowAdvanceOrder) {
        setPickup('later');
      }
    },
    [toggleOrderIfNotAllowed, navigateToStep, restaurant, showOrderingPaused],
  );

  useEffect(() => {
    if (restaurant?._id && !isRestoredNavigationRef.current) {
      // Ordering paused: no "closed" modal on arrival; the gates above say why
      // when the guest tries to order.
      if (restaurant?.orderingPaused) {
        return;
      }

      if (!restaurant?.isdinein && !restaurant?.istakeout) {
        setOrderFromRestaurantsNotAllowed(true);
        return;
      }

      if (!isOpenThisTime()) {
        setOrderNotAllowed(true);

        // if (restaurant?.allowAdvanceOrder) {
        //   setOrderType('take-away');
        //   setPickup('later');
        // }
      } else {
        setOrderNotAllowed(false); // Auto-dismiss when restaurant opens
      }
    }
  }, [orderType, pickup, restaurant, isOpenThisTime]);

  const isGuest = true;

  // The base the tip-dropdown labels are computed from. `discountApplied.amount`
  // is a mandatory dependency: calculateTotal is a plain function, so without it
  // the dropdown keeps offering a percentage of the pre-discount subtotal.
  const memoizedBaseAmount = useMemo(
    () => calculateTotal(true, hasBag, hasDeliveryFee, restaurant?.EnableWoltDelivery),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [hasBag, hasDeliveryFee, orderItems, restaurant?.EnableWoltDelivery, discountApplied.amount],
  );

  // The cart exactly as checkout posts it — no prices, the server prices every
  // line. Memoized on the cart so the lookup effect sees one stable fingerprint
  // per cart state instead of a new array on every render.
  const discountOrder = useMemo(
    () => formatOrdersForCheckout(orderItems),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [orderItems],
  );

  // The /discountcode/validate lookup lives HERE, on the page, and not inside the
  // field. The field is drawn by OrderInformation inside ReviewStep, and React
  // Router renders only the matching route — so the whole review subtree unmounts
  // the moment the guest steps on to `payment`. YourItems does not: it is
  // rendered outside <Routes> on both steps and keeps its live −/+ quantity
  // buttons, so the cart can still change with nothing watching it, and the
  // server-quoted amount would stay pinned to a basket that no longer exists.
  //
  // `restaurant` and `guestInfo` go in whole and the hook resolves the contact
  // and the currency itself — keep this call branch-free (the same
  // eslint-plugin-react-hooks cliff as the web page).
  const discountLocked = useDiscountCodeLookup({
    restaurantId,
    code: discountCode,
    order: discountOrder,
    mobilenumber: phoneNumber,
    guestInfo,
    restaurant,
    isGuest,
    guestId: orderContext.guestId,
    isDineIn: orderType === 'dine-in',
    applyNonce: discountApplyNonce,
    onResult: handleDiscountResult,
  });

  const isUserAllowedToOrder = true;

  const isDineIn = orderType === 'dine-in';
  const isAdvanceOrder = !isDineIn && pickup === 'later';

  const ChooseOrderStep = useMemo(
    () => (
      <OrderTypeSelection
        orderTypeOptions={orderTypeOptions}
        onContinue={handleOrderTypeSelectionContinue}
      />
    ),
    [orderTypeOptions, handleOrderTypeSelectionContinue],
  );

  const MenuStep = useMemo(
    () => (
      <>
        <RestaurantMenuHeader
          restaurant={restaurant}
          activeGroup={activeGroup}
          setActiveGroup={setActiveGroup}
          onViewOpeningTimesOpen={handleViewOpeningTimesOpen}
          fromKiosk={true}
        />

        {activeGroup === 'order-now' ? (
          <>
            {hasHigherCategories && (
              <CategoryTabs
                categories={categories}
                selectedCategory={selectedCategory}
                onSelectCategory={handleCategorySelect}
                fromKiosk={true}
              />
            )}

            <SubCategories
              ref={subNavRef}
              subCategories={currentSubCategories}
              activeSubCategory={activeSubCategory}
              onSelectSubCategory={handleSubCategorySelect}
              hasHigherCategories={hasHigherCategories}
              fromKiosk={true}
            />

            <MenuGrid
              restaurant={restaurant}
              categories={categories}
              subCategories={filteredSubCategories}
              groupedItems={filteredGroupedMenuItems}
              defaultImg={DEFAULT_IMG}
              onSelectItem={handleItemSelectionModalOpen}
              activeSubCategory={activeSubCategory}
              hasHigherCategories={hasHigherCategories}
            />
          </>
        ) : activeGroup === 'information' ? (
          <div className="py-[10px] md:py-[20px] lg:py-[30px] px-[15px] md:px-[25px] lg:px-[40px] min-h-[30vh]">
            {restaurantDescription ? (
              <div className="space-y-1">
                <h1 className="text-base sm:text-lg font-bold">
                  {translations[language]?.restaurant_menu_description_title}
                </h1>
                <h2 className="text-sm sm:text-base whitespace-pre-wrap">
                  {restaurantDescription}
                </h2>
              </div>
            ) : (
              <h2 className="text-center">
                {translations[language]?.restaurant_menu_no_description}
              </h2>
            )}
          </div>
        ) : null}
      </>
    ),
    [
      restaurant,
      activeGroup,
      categories,
      selectedCategory,
      currentSubCategories,
      filteredSubCategories,
      activeSubCategory,
      hasHigherCategories,
      filteredGroupedMenuItems,
      handleCategorySelect,
      handleSubCategorySelect,
      handleItemSelectionModalOpen,
      translations,
      language,
      subNavRef,
    ],
  );

  const ReviewStep = useMemo(
    () => (
      <div className="space-y-[16px] lg:space-y-[75px] order-[2] md:order-[1]">
        {isGuest && (
          <div className="restaurant-menu-section rounded-xl">
            <GuestInformation
              ref={guestInfoRef}
              guestInfo={guestInfo}
              setGuestInfo={setGuestInfo}
              phoneNumber={phoneNumber}
              onPhoneNumberChange={handlePhoneNumberChange}
              isValidPhone={isValidPhone}
              setIsValidPhone={setIsValidPhone}
              restaurant={restaurant}
            />
          </div>
        )}

        <div className="restaurant-menu-section rounded-xl">
          <OrderInformation
            restaurant={restaurant}
            restaurantId={restaurantId}
            setInAppLoading={setInAppLoading}
            orderType={orderType}
            orderTypeOptions={orderTypeOptions}
            onOrderTypeChange={handleOrderTypeChange}
            tableNumber={tableNumber}
            onTableNumberChange={handleTableNumberChange}
            tipId={tipId}
            tipPercentage={tipPercentage?.percentage || 0}
            onTipPercentageChange={handleTipPercentageChange}
            phoneNumber={phoneNumber}
            onPhoneNumberChange={handlePhoneNumberChange}
            comments={comments}
            onCommentsChange={handleCommentsChange}
            discountCode={discountCode}
            onDiscountCodeChange={handleDiscountCodeChange}
            onApplyDiscount={handleApplyDiscount}
            onRemoveDiscount={handleRemoveDiscount}
            discountLocked={discountLocked}
            discountStatus={discountStatus}
            discountAmount={discountApplied.amount}
            discountError={discountError}
            currency={restaurant?.currency?.code || 'DKK'}
            baseAmount={memoizedBaseAmount}
            isValidPhone={isValidPhone}
            setIsValidPhone={setIsValidPhone}
            pickup={pickup}
            pickupOptions={pickupOptions}
            onPickupChange={handlePickupChange}
            pickupDate={pickupDate}
            onPickupDateChange={handlePickupDateChange}
            pickupTime={pickupTime}
            onPickupTimeChange={handlePickupTimeChange}
            isPickupForTomorrow={isPickupForTomorrow}
            disabledTimes={getDisabledTimes(
              pickupDate,
              restaurant,
              lagTime,
              pickupTimeInterval,
              selectedCategory,
            )}
            pickupTimeInterval={pickupTimeInterval}
            roomNumber={roomNumber}
            onRoomNumberChange={handleRoomNumberChange}
            hasBag={hasBag}
            onBagToggle={handleBagToggle}
            bagFee={bagFee}
            hasDeliveryFee={hasDeliveryFee}
            onDeliveryFeeToggle={handleDeliveryFeeToggle}
            deliveryFee={deliveryFee}
            woltDelivery={woltDelivery}
            setWoltDelivery={setWoltDelivery}
            setWoltDeliveryFee={setWoltDeliveryFee}
            onWoltDeliveryFeeToggle={handleWoltDeliveryFeeToggle}
            woltDeliveryFee={woltDeliveryFee}
            tableNumbers={tableNumbers}
            restaurantTips={restaurantTips}
            ref={orderInfoRef}
            isGuest={isGuest}
            guestId={orderContext.guestId}
            fromKiosk={true}
            addressRef={addressRef}
            address={address}
            setAddress={setAddress}
            setIsErrorModalOpen={setIsErrorModalOpen}
            setErrorMessage={setErrorMessage}
          />
        </div>
      </div>
    ),
    [
      restaurant,
      restaurantId,
      setInAppLoading,
      orderType,
      orderTypeOptions,
      handleOrderTypeChange,
      tableNumber,
      handleTableNumberChange,
      tipId,
      tipPercentage,
      handleTipPercentageChange,
      phoneNumber,
      handlePhoneNumberChange,
      comments,
      handleCommentsChange,
      discountCode,
      handleDiscountCodeChange,
      handleApplyDiscount,
      handleRemoveDiscount,
      discountLocked,
      discountStatus,
      discountApplied.amount,
      discountError,
      memoizedBaseAmount,
      isValidPhone,
      pickup,
      pickupOptions,
      handlePickupChange,
      pickupDate,
      handlePickupDateChange,
      pickupTime,
      handlePickupTimeChange,
      isPickupForTomorrow,
      pickupTimeInterval,
      selectedCategory,
      roomNumber,
      handleRoomNumberChange,
      hasBag,
      handleBagToggle,
      bagFee,
      hasDeliveryFee,
      handleDeliveryFeeToggle,
      deliveryFee,
      woltDelivery,
      setWoltDelivery,
      setWoltDeliveryFee,
      handleWoltDeliveryFeeToggle,
      woltDeliveryFee,
      tableNumbers,
      restaurantTips,
      isGuest,
      orderContext.guestId,
      guestInfo,
      setGuestInfo,
      setIsValidPhone,
      addressRef,
      address,
      setAddress,
      setIsErrorModalOpen,
      setErrorMessage,
    ],
  );

  const PaymentStep = useMemo(
    () => (
      <div ref={paymentRef}>
        <Payment
          onCapacityFull={(nextSlot) =>
            setCapacityFullModal({ open: true, nextSlot: nextSlot || null })
          }
          orderItems={orderItems}
          setOrderItems={setOrderItems}
          totalPrice={grandTotal()}
          currency={restaurant?.currency?.code || 'DKK'}
          restaurant={restaurant}
          userProfile={userProfile}
          onPayment={handleProcessPayment}
          addNewCard={addNewCard}
          onPersistOrderData={handlePersistOrderData}
          resetOrderInfo={resetOrderInfo}
          formatOrdersForCheckout={formatOrdersForCheckout}
          isDineIn={isDineIn}
          tableNumbers={tableNumbers}
          tableNumber={tableNumber}
          setTableNumber={setTableNumber}
          tipAmount={tipAmount}
          bagFee={bagFee}
          deliveryFee={deliveryFee}
          hasBag={hasBag}
          hasDeliveryFee={hasDeliveryFee}
          woltDelivery={woltDelivery}
          tipId={tipId}
          comments={comments}
          setComments={setComments}
          phoneNumber={phoneNumber}
          isGuest={isGuest}
          guestId={orderContext.guestId}
          pickup={pickup}
          pickupDate={pickupDate}
          pickupTime={pickupTime}
          roomNumber={roomNumber}
          currentStep={currentStep}
          navigateToStep={navigateToStep}
          tipPercentage={tipPercentage}
          setTipPercentage={setTipPercentage}
          fromKiosk={true}
          discountCode={discountApplied.valid ? discountApplied.code : ''}
          discountAmount={discountApplied.amount}
          onDiscountCodeRejected={handleDiscountCodeRejected}
        />
      </div>
    ),
    [
      orderItems,
      setOrderItems,
      grandTotal,
      restaurant,
      userProfile,
      handleProcessPayment,
      addNewCard,
      handlePersistOrderData,
      resetOrderInfo,
      formatOrdersForCheckout,
      isDineIn,
      tableNumbers,
      tableNumber,
      setTableNumber,
      tipAmount,
      bagFee,
      deliveryFee,
      hasBag,
      hasDeliveryFee,
      woltDelivery,
      tipId,
      comments,
      setComments,
      phoneNumber,
      isGuest,
      orderContext.guestId,
      pickup,
      pickupDate,
      pickupTime,
      roomNumber,
      currentStep,
      navigateToStep,
      tipPercentage,
      setTipPercentage,
      // MANDATORY: this memo passes totalPrice={grandTotal()}, a plain function
      // call, so without the discount amount here the payment step works off a
      // stale, pre-discount total.
      discountApplied.valid,
      discountApplied.code,
      discountApplied.amount,
      handleDiscountCodeRejected,
    ],
  );

  if (loading) return <Loader />;
  if (error) return <div>{translations[language]?.restaurant_menu_no_data}</div>;
  if (!restaurant) return null;
  if (restaurant.orderingPaused) {
    return (
      <OrderingPausedScreen message={restaurant.orderingPausedMessage} />
    );
  }

  return (
    <div
      className="restaurant-menu-container max-w-[80rem] mx-auto p-2 sm:p-4 !pb-[120px] sm:!pb-[100px]"
      style={{
        '--primary': restaurant?.PrimaryColor || '#96c11f',
        '--secondary': restaurant?.SecondaryColor || '#6f9800',
        '--text': restaurant?.TextColor || '#ffffff',
      }}
    >
      {inAppLoading && <Loader />}

      {/* Routes for menu step */}
      <Routes>
        <Route path="menu" element={MenuStep} />
        <Route path="" element={<Navigate to="menu" replace />} />
      </Routes>

      {/* Conditional layout with routes for review and payment steps */}
      {(currentStep === 'review' || currentStep === 'payment') && (
        <div className="grid grid-cols-1 md:grid-cols-[auto_300px] lg:grid-cols-[auto_404px] xl:grid-cols-[auto_454px] gap-[16px] xl:gap-[75px] items-start">
          <div className="space-y-[16px] lg:space-y-[75px] order-[2] md:order-[1]">
            <Routes>
              <Route path="review" element={ReviewStep} />
              <Route path="payment" element={PaymentStep} />
            </Routes>
          </div>

          <div className="restaurant-menu-section rounded-xl order-[1] md:order-[2]">
            <YourItems
              restaurant={restaurant}
              categories={categories}
              items={orderItems}
              onUpdateQuantity={handleUpdateItemQuantity}
              onRemoveItem={handleRemoveFromOrder}
              onEditItem={editCartItem}
              tipPercentage={tipPercentage?.percentage || 0}
              tipAmount={tipAmount}
              discountCode={discountApplied.valid ? discountApplied.code : ''}
              discountAmount={discountApplied.valid ? Number(discountApplied.amount) || 0 : 0}
              total={calculateTotal(true, true, true, true)}
              grandTotal={grandTotal()}
              grandTotalBeforeCode={grandTotal(true)}
              calculatedServiceFee={calculatedServiceFee()}
              currency={restaurant?.currency?.code || 'DKK'}
              onPayment={handlePayment}
              defaultImg={DEFAULT_IMG}
              orderType={orderType}
              hasBag={hasBag}
              bagFee={bagFee}
              hasDeliveryFee={hasDeliveryFee}
              deliveryFee={deliveryFee}
              woltDelivery={woltDelivery}
              woltDeliveryFee={woltDeliveryFee}
              hasServiceFee={hasServiceFee}
              serviceFee={serviceFee}
              hasFlatFee={hasFlatFee}
              flatFee={flatFee}
              pickupDate={pickupDate}
              pickupTime={pickupTime}
              currentStep={currentStep}
              onGoToSelectPaymentMethod={handleGoToSelectPaymentMethod}
              ref={yourItemsRef}
            />
          </div>
        </div>
      )}

      <OrderSummary
        items={orderItems}
        grandTotal={grandTotal()}
        onViewCart={handleViewCart}
        orderType={orderType}
        orderTypeOptions={orderTypeOptions}
        onOrderTypeChange={handleOrderTypeChange}
        restaurant={restaurant}
        currentStep={currentStep}
        onNextStep={handleNextStep}
        onPrevStep={handlePrevStep}
        onGoToSelectPaymentMethod={handleGoToSelectPaymentMethod}
      />

      {/* Modals */}
      {selectedItem && (
        <Modal
          isOpen={isItemSelectionModalOpen}
          onClose={() => setIsItemSelectionModalOpen(false)}
          headerImg={selectedItem?.image || DEFAULT_IMG || restaurant?.MenuPlaceholder}
          FooterComponent={ItemSelectionFooter}
          noPadding
        >
          <div className="flex flex-col">
            <div className="p-4 space-y-2 sm:space-y-4 border-b-[1px] border-[#f5f5f5]">
              <h1 className="text-lg sm:text-xl font-semibold">{selectedItem?.name}</h1>
              <p className="text-sm text-[#707070] whitespace-pre-wrap">
                {selectedItem?.description}
              </p>
              <h2 className="text-sm font-semibold">
                {restaurant?.currency?.code || 'DKK'} {selectedItem?.price}
              </h2>
            </div>

            {extrasItems.length > 0 && (
              <div className="p-4 border-b-[1px] border-[#f5f5f5]">
                <h2 className="text-md lg:text-lg font-semibold mb-4">
                  {translations[language]?.restaurant_menu_options}
                </h2>
                <MultiCounter
                  items={extrasItems}
                  onChange={handleExtrasChange}
                  currency={restaurant?.currency?.code || 'DKK'}
                  selectedValues={selectedExtras}
                  totalSelectedCount={0} // Extras don't count towards the total limit
                />
              </div>
            )}

            {groupedAttributes.map((attribute) => {
              // Find attribute in selectedAttributes
              const attributeSelection = selectedAttributes.find(
                (attr) => attr.attributeId?.toString() === attribute._id?.toString(),
              );
              const selectedOptions = attributeSelection ? attributeSelection.options : [];
              const isRequired = attribute.attributesRequired;

              // Handle connected attributes for options
              const connectedAttributes = selectedItem?.attributes
                ?.filter((attr) => {
                  if (!attr.belongsto) return false;

                  return selectedOptions.some((selection) => {
                    if (!selection?.isconnectedto || !Array.isArray(selection?.isconnectedto)) {
                      return false;
                    }

                    const attrIdStr = String(attr._id);
                    return selection?.isconnectedto.some(
                      (connectedId) => String(connectedId) === attrIdStr,
                    );
                  });
                })
                .map((attr) => {
                  const selectedOption = selectedOptions.find((selection) => {
                    if (!selection?.isconnectedto || !Array.isArray(selection?.isconnectedto)) {
                      return false;
                    }

                    const attrIdStr = String(attr._id);
                    return selection?.isconnectedto.some(
                      (connectedId) => String(connectedId) === attrIdStr,
                    );
                  });

                  if (selectedOption) {
                    return {
                      ...attr,
                      id: attr._id,
                    };
                  }

                  return {
                    ...attr,
                    id: attr._id,
                  };
                });

              return (
                <div key={attribute._id} className="p-4 border-b-[1px] border-[#f5f5f5]">
                  <h2 className="text-md lg:text-lg font-semibold mb-2">
                    {attribute.name}
                    {isRequired && (
                      <span className="text-sm text-red-500 ml-1">
                        &#40;{translations[language]?.restaurant_menu_required}&#41;
                      </span>
                    )}
                  </h2>

                  {attribute.isMultiple ? (
                    <MultiCounter
                      items={
                        attribute.options.map((option) => ({
                          _id: option.id,
                          id: option.id,
                          label: option.name,
                          price: option.additionalPrice,
                          initialCount: 0,
                          additionalPrice: option.additionalPrice,
                          limit: option.limit || null,
                          minlimit: option.minlimit || null,
                          isconnectedto: option?.isconnectedto || null,
                        })) || []
                      }
                      onChange={(newCounts, itemId) =>
                        handleMultipleCounterChange(attribute._id, itemId, newCounts)
                      }
                      currency={restaurant?.currency?.code || 'DKK'}
                      maxOptionCount={attribute.limitPerCategory}
                      selectedValues={selectedOptions}
                      attribute={attribute}
                      totalSelectedCount={
                        // Only sum up quantities from selectedAttributes
                        selectedAttributes?.reduce?.(
                          (total, attr) =>
                            total +
                            attr?.options?.reduce?.((sum, opt) => sum + (opt?.quantity || 1), 0),
                          0,
                        )
                      }
                      connectedAttributes={connectedAttributes}
                      onConnectedAttributeChange={handleRadioAttributeChange}
                      selectedAttributes={selectedAttributes}
                    />
                  ) : (
                    <RadioGroup
                      options={attribute.options}
                      onChange={(selectedOption) =>
                        handleRadioAttributeChange(attribute._id, selectedOption)
                      }
                      currency={restaurant?.currency?.code || 'DKK'}
                      required={isRequired}
                      selectedOption={selectedOptions.length > 0 ? selectedOptions[0] : null}
                      connectedAttributes={connectedAttributes}
                      onConnectedAttributeChange={handleRadioAttributeChange}
                      selectedAttributes={selectedAttributes}
                    />
                  )}
                </div>
              );
            })}

            <div className="p-4 border-b-[1px] border-[#f5f5f5]">
              <h2 className="text-md lg:text-lg font-semibold mb-4">
                {translations[language]?.comments}
              </h2>
              <textarea
                className="w-full !p-3 sm:!p-4 bg-[#f5f5f5] rounded-xl h-[150px] resize-none text-sm sm:text-base"
                placeholder={`${translations[language]?.comments_placeholder}...`}
                value={itemComments}
                onChange={(e) => setItemComments(e.target.value)}
              />
            </div>
          </div>
        </Modal>
      )}

      <RestaurantAvailabilityModal
        isOpen={isViewOpeningTimesOpen}
        onClose={() => setIsViewOpeningTimesOpen(false)}
        availability={restaurant?.availability}
      />

      <ValidationErrorModal
        isOpen={isValidationErrorModalOpen}
        onClose={() => {
          setIsValidationErrorModalOpen(false);
          setValidationErrorMessage('');
        }}
        validationErrorMessage={validationErrorMessage}
      />

      <MenuNotAvailableModal isOpen={menuNotAvailable} onClose={() => setMenuNotAvailable(false)} />

      <OrderNotAllowedModal
        isOpen={orderNotAllowed}
        onClose={() => setOrderNotAllowed(false)}
        allowAdvanceOrder={restaurant?.allowAdvanceOrder}
        isTakeAway={restaurant?.istakeout}
        fromKiosk={true}
      />

      <RushModeModal
        restaurant={restaurant}
        isOpen={isRushModeModalOpen}
        onClose={() => setIsRushModeModalOpen(false)}
      />

      <CapacityFullModal
        isOpen={capacityFullModal.open}
        onClose={() => setCapacityFullModal({ open: false, nextSlot: null })}
        nextAvailableSlot={capacityFullModal.nextSlot}
        isDineIn={orderType === 'dine-in'}
        onPickAnotherTime={() => {
          setCapacityFullModal({ open: false, nextSlot: null });
          if (currentStep !== 'review') navigateToStep('review');
          setTimeout(() => {
            orderInfoRef.current?.scrollToPickupTime?.();
          }, 200);
        }}
      />

      <PromoPopupModal
        restaurant={restaurant}
        isOpen={isPromoPopupModalOpen}
        onClose={() => setIsPromoPopupModalOpen(false)}
      />

      <ProcessingOrderModal
        isOpen={isProcessingOrderModalOpen}
        onClose={() => {
          setIsProcessingOrderModalOpen(false);
        }}
        title={translations[language]?.restaurant_menu_order_thank_you}
        noPadding
        noCloseButton={orderStatus?.isPending}
        restaurant={restaurant}
        orderType={orderType}
        payConfirm={payConfirm}
        orderStatus={orderStatus}
        cancelOrder={cancelOrder}
        isCancelling={isCancelling}
        fromKiosk={true}
      />

      <ErrorModal
        isOpen={isErrorModalOpen}
        onClose={() => {
          setIsErrorModalOpen(false);
          setErrorMessage('');
        }}
        errorMessage={errorMessage}
      />
    </div>
  );
}
